import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";

/* ── THEME ──────────────────────────────────────────────────── */
const T = {
  bg:"#080F1A", surface:"#0E1825", card:"#131F2E", cardHover:"#172333",
  border:"#1E2E42", borderHi:"#2A3F58",
  blue:"#2563EB", blueLight:"#3B82F6", blueFaint:"#1E3A5F",
  amber:"#D97706", amberFaint:"#2C1F0A",
  text:"#E2EAF4", textSub:"#8BA5C0", textMuted:"#4F6880",
  success:"#059669", successFaint:"#052E1C",
  danger:"#DC2626", dangerFaint:"#2D0F0F",
  teal:"#0D9488", tealFaint:"#062320", pro:"#7C3AED",
};

/* ── RESPONSIVE ─────────────────────────────────────────────── */
function useIsMobile() {
  const [m, setM] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setM(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return m;
}

/* ── DATA ───────────────────────────────────────────────────── */
const TRADES = ["All Trades","Electrician","Plumber","Roofer","HVAC","Carpenter","Welder","Mason","Painter","General Contractor","Drywall","Landscaper","Flooring","Tile","Multiple Trades","Other"];
const TRADES_SELECT = ["Electrician","Plumber","Roofer","HVAC","Carpenter","Welder","Mason","Painter","General Contractor","Drywall","Landscaper","Flooring","Tile","Multiple Trades","Other"];

const CONTRACTORS = [];
const JOBS = [];
const MY_ACTIVE_JOBS = [];
const MY_BIDS = [];
const MY_CONTRACTS = [];
const CLIENT_PROJECTS = [];
const MSGS = [];
const CHAT_INIT = [];


/* ── AI ─────────────────────────────────────────────────────── */
async function genContract(f) {
  const r = await fetch("https://api.anthropic.com/v1/messages", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ model:"claude-sonnet-4-20250514", max_tokens:1000, messages:[{ role:"user", content:`Generate a professional trades work contract.\nContractor: ${f.contractor} (${f.trade})\nClient: ${f.client}\nAddress: ${f.address}\nScope: ${f.description}\nAmount: $${f.amount}\nStart: ${f.startDate}\nDuration: ${f.duration}\nType: ${f.contractType}\nUse ALL CAPS headers: PARTIES, SCOPE OF WORK, PAYMENT TERMS, TIMELINE, CHANGE ORDERS, WARRANTY, LIABILITY, DISPUTE RESOLUTION, SIGNATURES.` }] }) });
  const d = await r.json(); return d.content?.[0]?.text || "Contract generation failed.";
}

async function aiChat(history, msg) {
  const msgs = [...history.map(m => ({ role:m.from==="me"?"assistant":"user", content:m.text })), { role:"user", content:msg }];
  const r = await fetch("https://api.anthropic.com/v1/messages", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ model:"claude-sonnet-4-20250514", max_tokens:1000, system:"You are a homeowner chatting with a contractor on BluTrades about a home improvement project. Reply briefly and naturally like a real text conversation.", messages:msgs }) });
  const d = await r.json(); return d.content?.[0]?.text || "Sounds good.";
}

/* ── SHARED COMPONENTS ──────────────────────────────────────── */
function Ini({ initials, color, size=44 }) {
  return <div style={{ width:size, height:size, borderRadius:"50%", flexShrink:0, background:`${color}22`, border:`1.5px solid ${color}44`, display:"flex", alignItems:"center", justifyContent:"center", color, fontWeight:700, fontSize:size*0.34, letterSpacing:.5 }}>{initials}</div>;
}

function OnlineDot({ online }) {
  return online ? <div style={{ width:8, height:8, borderRadius:"50%", background:"#10B981", border:`2px solid ${T.card}`, position:"absolute", bottom:1, right:1 }}/> : null;
}

function Card({ children, style={}, onClick }) {
  const [hov, setHov] = useState(false);
  return <div onClick={onClick} onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)} style={{ background:hov&&onClick?T.cardHover:T.card, borderRadius:10, border:`1px solid ${hov&&onClick?T.borderHi:T.border}`, padding:"20px 22px", transition:"all .15s", cursor:onClick?"pointer":"default", ...style }}>{children}</div>;
}

function Btn({ children, variant="primary", onClick, style={}, full, sm }) {
  const pad = sm ? "7px 14px" : "10px 20px";
  const fs = sm ? 13 : 14;
  const v = {
    primary: { background:T.blue,         color:"#fff",      border:"none"                     },
    ghost:   { background:"transparent",  color:T.textSub,   border:`1px solid ${T.border}`    },
    subtle:  { background:T.blueFaint,    color:T.blueLight, border:`1px solid ${T.blueFaint}` },
    danger:  { background:T.dangerFaint,  color:T.danger,    border:`1px solid ${T.danger}22`  },
    success: { background:T.successFaint, color:T.success,   border:`1px solid ${T.success}33` },
    amber:   { background:T.amberFaint,   color:T.amber,     border:`1px solid ${T.amber}33`   },
    teal:    { background:T.teal,         color:"#fff",      border:"none"                     },
  };
  return <button onClick={onClick} style={{ ...v[variant], borderRadius:8, padding:pad, fontWeight:600, fontSize:fs, cursor:"pointer", transition:"opacity .15s", width:full?"100%":"auto", ...style }}>{children}</button>;
}

function Input({ label, placeholder, value, onChange, multiline, rows=3, type="text" }) {
  const [showPw, setShowPw] = useState(false);
  const isPassword = type === "password";
  return (
    <div style={{ marginBottom:14 }}>
      {label && <div style={{ fontSize:12, color:"#C8DCF0", marginBottom:6, fontWeight:700, letterSpacing:.6, textTransform:"uppercase" }}>{label}</div>}
      {multiline
        ? <textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows} style={{ width:"100%", background:T.surface, border:`1px solid ${T.border}`, borderRadius:8, padding:"10px 14px", color:T.text, fontSize:14, resize:"none", outline:"none", lineHeight:1.6 }}/>
        : <div style={{ position:"relative" }}>
            <input value={value} onChange={onChange} placeholder={placeholder} type={isPassword?(showPw?"text":"password"):type} style={{ width:"100%", background:T.surface, border:`1px solid ${T.border}`, borderRadius:8, padding:isPassword?"10px 44px 10px 14px":"10px 14px", color:T.text, fontSize:14, outline:"none" }}/>
            {isPassword && (
              <button onClick={()=>setShowPw(!showPw)} type="button" style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:T.textMuted, fontSize:16, padding:0, lineHeight:1 }}>
                {showPw ? "🙈" : "👁"}
              </button>
            )}
          </div>
      }
    </div>
  );
}

function Badge({ color, children }) {
  return <span style={{ background:`${color}18`, color, border:`1px solid ${color}30`, borderRadius:5, padding:"2px 8px", fontSize:11, fontWeight:600, whiteSpace:"nowrap", letterSpacing:.3 }}>{children}</span>;
}

function Stars({ n, size=13 }) {
  return <span style={{ color:T.amber, fontSize:size }}>{"★".repeat(Math.floor(n))}<span style={{ color:T.textMuted, marginLeft:4, fontSize:size-1 }}>{n}</span></span>;
}

function ProgBar({ pct, color=T.blue, thin }) {
  const h = thin ? 3 : 5;
  return <div style={{ background:`${color}18`, borderRadius:h, height:h, overflow:"hidden" }}><div style={{ width:`${pct}%`, height:"100%", background:color, borderRadius:h, transition:"width .4s" }}/></div>;
}

function Divider() { return <div style={{ height:1, background:T.border, margin:"16px 0" }}/>; }

function SH({ title, sub, action, onAction }) {
  return (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", marginBottom:16 }}>
      <div><div style={{ fontWeight:700, fontSize:16, color:T.text }}>{title}</div>{sub&&<div style={{ color:T.textMuted, fontSize:13, marginTop:3 }}>{sub}</div>}</div>
      {action&&<button onClick={onAction} style={{ background:"none", border:"none", color:T.blueLight, fontSize:13, fontWeight:600, cursor:"pointer" }}>{action}</button>}
    </div>
  );
}

function Modal({ open, onClose, children, title }) {
  if (!open) return null;
  return (
    <div style={{ position:"fixed", inset:0, background:"#00000099", display:"flex", alignItems:"center", justifyContent:"center", zIndex:300, backdropFilter:"blur(4px)", padding:20 }}>
      <div style={{ background:T.surface, borderRadius:12, padding:"28px", width:"100%", maxWidth:540, border:`1px solid ${T.border}`, maxHeight:"90vh", overflowY:"auto", boxShadow:"0 20px 60px #00000066" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:22 }}>
          <div style={{ fontWeight:700, fontSize:18, color:T.text }}>{title}</div>
          <button onClick={onClose} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:6, width:32, height:32, color:T.textSub, fontSize:16, cursor:"pointer" }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function StatusDot({ status }) {
  const colors = { "In Progress":T.blue, "Awaiting Payment":T.amber, "Completed":T.success, "Awaiting Review":T.amber, "Seeking Bids":T.textMuted, "Awaiting Signature":T.amber };
  return <span style={{ display:"inline-flex", alignItems:"center", gap:5 }}><span style={{ width:6, height:6, borderRadius:"50%", background:colors[status]||T.textMuted, display:"inline-block" }}/><span style={{ fontSize:12, color:colors[status]||T.textMuted, fontWeight:600 }}>{status}</span></span>;
}

/* ── FILE UPLOAD COMPONENT ──────────────────────────────────── */
function FileUploadBtn({ label, note, onUpload, uploaded, fileName }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    await onUpload(file);
    setUploading(false);
  };

  return (
    <div style={{ background:T.surface, borderRadius:8, padding:"12px 14px", marginBottom:8, border:`1px solid ${uploaded?T.success+"44":T.border}` }}>
      <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" style={{ display:"none" }} onChange={handleFile}/>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <div style={{ fontWeight:600, color:T.text, fontSize:14 }}>{label}</div>
          <div style={{ color:uploaded?T.success:T.textMuted, fontSize:12, marginTop:2 }}>
            {uploaded ? `✓ Uploaded: ${fileName}` : note}
          </div>
        </div>
        <Btn variant={uploaded?"success":"subtle"} sm onClick={() => inputRef.current?.click()}>
          {uploading ? "Uploading..." : uploaded ? "Replace" : "Upload"}
        </Btn>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   AUTH SCREEN
══════════════════════════════════════════════════════════════ */
function AuthScreen({ onAuth }) {
  const [mode, setMode] = useState("login");
  const [role, setRole] = useState("contractor");
  const [trade, setTrade] = useState([]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [location, setLocation] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState(1);
  const isMobile = useIsMobile();

  const handleLogin = async () => {
    if (!email || !password) { setError("Please enter your email and password."); return; }
    setLoading(true); setError("");
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      const { data: profile } = await supabase.from("profiles").select("*").eq("id", data.user.id).single();
      onAuth(data.user, profile);
    } catch (err) { setError(err.message || "Login failed."); }
    setLoading(false);
  };

  const handleSignup = async () => {
    if (!email || !password || !fullName) { setError("Please fill in all fields."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setLoading(true); setError("");
    try {
      const { data, error } = await supabase.auth.signUp({ email, password, options: { data: { full_name: fullName, role } } });
      if (error) throw error;
      if (data.user) {
        await supabase.from("profiles").upsert({ id: data.user.id, full_name: fullName, email, role, location });
        // Force update location separately to ensure it saves
        if (location) {
          await supabase.from("profiles").update({ location, full_name: fullName }).eq("id", data.user.id);
        }
        if (role === "contractor") await supabase.from("contractor_profiles").upsert({ id: data.user.id, trade: trade[0]||"General Contractor", trades: trade.join(",") });
        else await supabase.from("client_profiles").upsert({ id: data.user.id });
        const { data: profile } = await supabase.from("profiles").select("*").eq("id", data.user.id).single();
        onAuth(data.user, profile);
      }
    } catch (err) { setError(err.message || "Signup failed."); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight:"100vh", background:T.bg, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ maxWidth:440, width:"100%" }}>
        <div style={{ textAlign:"center", marginBottom:36 }}>
          <div style={{ fontWeight:800, fontSize:32, color:T.text, letterSpacing:-1 }}>Blu<span style={{ color:T.blue }}>Trades</span></div>
          <div style={{ color:T.textMuted, fontSize:15, marginTop:8 }}>The professional network for the trades industry</div>
        </div>
        <Card style={{ padding:"28px 32px" }}>
          <div style={{ background:T.surface, borderRadius:7, padding:3, display:"flex", marginBottom:22, border:`1px solid ${T.border}` }}>
            <button onClick={() => { setMode("login"); setError(""); setStep(1); }} style={{ flex:1, background:mode==="login"?T.blue:"transparent", color:mode==="login"?"#fff":T.textMuted, border:"none", borderRadius:5, padding:"9px", fontSize:13, fontWeight:600, cursor:"pointer" }}>Sign In</button>
            <button onClick={() => { setMode("signup"); setError(""); setStep(1); }} style={{ flex:1, background:mode==="signup"?T.blue:"transparent", color:mode==="signup"?"#fff":T.textMuted, border:"none", borderRadius:5, padding:"9px", fontSize:13, fontWeight:600, cursor:"pointer" }}>Create Account</button>
          </div>

          {mode==="login" && <>
            <Input label="Email" placeholder="your@email.com" value={email} onChange={e=>setEmail(e.target.value)} type="email"/>
            <Input label="Password" placeholder="Your password" value={password} onChange={e=>setPassword(e.target.value)} type="password"/>
            {error && <div style={{ color:T.danger, fontSize:13, marginBottom:14, background:T.dangerFaint, padding:"10px 14px", borderRadius:8, border:`1px solid ${T.danger}33` }}>{error}</div>}
            <Btn variant="primary" full onClick={handleLogin} style={{ padding:"12px", fontSize:15, marginTop:4 }}>{loading?"Signing in...":"Sign In"}</Btn>
            <div style={{ textAlign:"center", marginTop:14, color:T.textMuted, fontSize:13 }}>Don't have an account? <button onClick={()=>setMode("signup")} style={{ background:"none", border:"none", color:T.blueLight, cursor:"pointer", fontWeight:600 }}>Create one</button></div>
          </>}

          {mode==="signup" && step===1 && <>
            <div style={{ fontWeight:700, fontSize:16, color:T.text, marginBottom:14 }}>I am a...</div>
            <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:18 }}>
              {[["contractor","Contractor / Tradesperson","Find work, get verified, build your reputation"],["client","Homeowner / Client","Post jobs and hire verified tradespeople"]].map(([r,label,sub])=>(
                <div key={r} onClick={()=>setRole(r)} style={{ background:role===r?T.blueFaint:T.surface, border:`1px solid ${role===r?T.blue+"44":T.border}`, borderRadius:8, padding:"14px 16px", cursor:"pointer" }}>
                  <div style={{ fontWeight:600, fontSize:14, color:T.text, marginBottom:3 }}>{label}</div>
                  <div style={{ color:T.textMuted, fontSize:13 }}>{sub}</div>
                </div>
              ))}
            </div>
            <Btn variant="primary" full onClick={()=>setStep(2)} style={{ padding:"12px" }}>Continue</Btn>
          </>}

          {mode==="signup" && step===2 && role==="contractor" && <>
            <div style={{ fontWeight:700, fontSize:16, color:T.text, marginBottom:6 }}>Your trades</div>
            <div style={{ color:T.textMuted, fontSize:13, marginBottom:14 }}>Select up to 3 trades. Your first selection is your primary trade.</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:7, marginBottom:10 }}>
              {TRADES_SELECT.map(t=>{
                const selected = trade.includes(t);
                const maxed = trade.length >= 3 && !selected;
                return <button key={t} onClick={()=>{
                  if(selected) setTrade(prev=>prev.filter(x=>x!==t));
                  else if(!maxed) setTrade(prev=>[...prev,t]);
                }} style={{ background:selected?T.blue:T.surface, color:selected?"#fff":maxed?"#ffffff44":T.textSub, border:`1px solid ${selected?T.blue:T.border}`, borderRadius:6, padding:"6px 13px", fontSize:12, fontWeight:600, cursor:maxed?"not-allowed":"pointer", opacity:maxed?0.4:1 }}>
                  {t}{selected&&trade.indexOf(t)===0?" ★":""}
                </button>;
              })}
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <div style={{ color:trade.length>=3?T.amber:T.textMuted, fontSize:13, fontWeight:600 }}>
                {trade.length}/3 selected{trade.length>=3?" · Maximum reached":""}
              </div>
              {trade.length>0&&<div style={{ color:T.textMuted, fontSize:12 }}>★ = primary trade</div>}
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <Btn variant="ghost" onClick={()=>setStep(1)} style={{ flex:1 }}>Back</Btn>
              <Btn variant="primary" onClick={()=>trade.length>0&&setStep(3)} style={{ flex:2, padding:"11px" }}>Continue</Btn>
            </div>
          </>}

          {mode==="signup" && ((step===2 && role==="client") || (step===3 && role==="contractor")) && <>
            <div style={{ fontWeight:700, fontSize:16, color:T.text, marginBottom:14 }}>Your details</div>
            <Input label="Full Name" placeholder="First and last name" value={fullName} onChange={e=>setFullName(e.target.value)}/>
            <Input label="Email" placeholder="your@email.com" value={email} onChange={e=>setEmail(e.target.value)} type="email"/>
            <Input label="Password" placeholder="At least 6 characters" value={password} onChange={e=>setPassword(e.target.value)} type="password"/>
            <Input label="City & State" placeholder="Houston, TX" value={location} onChange={e=>setLocation(e.target.value)}/>
            {error && <div style={{ color:T.danger, fontSize:13, marginBottom:14, background:T.dangerFaint, padding:"10px 14px", borderRadius:8, border:`1px solid ${T.danger}33` }}>{error}</div>}
            <div style={{ display:"flex", gap:10 }}>
              <Btn variant="ghost" onClick={()=>setStep(step-1)} style={{ flex:1 }}>Back</Btn>
              <Btn variant={role==="client"?"teal":"primary"} onClick={handleSignup} style={{ flex:2, padding:"11px" }}>{loading?"Creating account...":"Create Account"}</Btn>
            </div>
          </>}
        </Card>
        <div style={{ textAlign:"center", marginTop:16, color:T.textMuted, fontSize:12 }}>By continuing you agree to BluTrades <span style={{ color:T.blueLight, cursor:"pointer" }}>Terms of Service</span> and <span style={{ color:T.blueLight, cursor:"pointer" }}>Privacy Policy</span></div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   CONTRACTOR SCREENS
══════════════════════════════════════════════════════════════ */
function CDashboard({ user, profile, setTab, setSelected, isMobile }) {
  const name = profile?.full_name?.split(" ")[0] || "there";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const [nearbyJobs, setNearbyJobs] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [stats, setStats] = useState({ earned: 0, active: 0, completed: 0, reviews: 0 });

  useEffect(()=>{
    loadDashboardData();
  }, [user?.id]);

  const loadDashboardData = async() => {
    if(!user?.id) return;

    // Load nearby jobs (open jobs not already bid on)
    const {data: jobs} = await supabase
      .from("jobs")
      .select("*")
      .eq("status", "open")
      .order("created_at", {ascending: false})
      .limit(3);

    if(jobs) {
      setNearbyJobs(jobs.map(j=>({
        id: j.id,
        title: j.title,
        trade: j.trade||"General",
        loc: j.location||"Nearby",
        budget: j.budget_display ? `$${j.budget_display}` : "Contact for rate",
        posted: new Date(j.created_at).toLocaleDateString(),
        urgent: j.urgency==="urgent",
      })));
    }

    // Load my recent bids
    const {data: bids} = await supabase
      .from("bids")
      .select("*, jobs(title, client_id, profiles:client_id(full_name))")
      .eq("contractor_id", user.id)
      .order("created_at", {ascending: false})
      .limit(4);

    if(bids) {
      setMyBids(bids.map(b=>({
        id: b.id,
        job: b.jobs?.title||"Job",
        client: b.jobs?.profiles?.full_name||"Client",
        amount: `$${Number(b.amount||0).toLocaleString()}`,
        status: b.status==="accepted"?"Accepted":b.status==="declined"?"Declined":"Pending",
        date: new Date(b.created_at).toLocaleDateString(),
      })));
    }

    // Load stats
    const [{data: activeBids}, {data: completedJobs}, {data: reviews}] = await Promise.all([
      supabase.from("bids").select("id").eq("contractor_id", user.id).eq("status","accepted"),
      supabase.from("jobs").select("id").eq("hired_contractor_id", user.id).eq("status","completed"),
      supabase.from("reviews").select("id").eq("contractor_id", user.id),
    ]);

    setStats({
      earned: 0,
      active: activeBids?.length||0,
      completed: completedJobs?.length||0,
      reviews: reviews?.length||0,
    });
  };

  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <div style={{ color:T.textMuted, fontSize:14, marginBottom:4 }}>{greeting}, {name}</div>
        <div style={{ fontWeight:700, fontSize:isMobile?24:30, color:T.text, letterSpacing:-.5 }}>Here's your overview</div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4,1fr)", gap:14, marginBottom:24 }}>
        {[["Earned this month","$"+stats.earned.toLocaleString(),T.success],["Active jobs",stats.active,T.blue],["Jobs completed",stats.completed,T.text],["Reviews",stats.reviews,T.text]].map(([l,v,c],i)=>(
          <Card key={i} style={{ padding:"18px 20px" }}><div style={{ color:T.textMuted, fontSize:13, marginBottom:8 }}>{l}</div><div style={{ fontWeight:700, fontSize:26, color:c, letterSpacing:-.5 }}>{v}</div></Card>
        ))}
      </div>
      <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:20, marginBottom:24 }}>
        <div>
          <SH title="Active Jobs" action="Job Tracker" onAction={()=>setTab("c_tracker")}/>
          {MY_ACTIVE_JOBS.length === 0 ? (
            <Card style={{ padding:"24px", textAlign:"center" }}>
              <div style={{ color:T.textMuted, fontSize:14, marginBottom:10 }}>No active jobs yet</div>
              <Btn variant="subtle" sm onClick={()=>setTab("c_jobs")}>Find Work</Btn>
            </Card>
          ) : MY_ACTIVE_JOBS.map(j=>(
            <Card key={j.id} style={{ marginBottom:10 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                <div><div style={{ fontWeight:600, fontSize:14, color:T.text, marginBottom:4 }}>{j.title}</div><div style={{ color:T.textMuted, fontSize:13 }}>{j.amount} · Started {j.start}</div></div>
                <StatusDot status={j.status}/>
              </div>
              <ProgBar pct={j.pct} color={j.status==="Awaiting Payment"?T.amber:T.blue}/>
            </Card>
          ))}
        </div>
        <div>
          <SH title="Jobs Near You" action="Browse All" onAction={()=>setTab("c_jobs")}/>
          {nearbyJobs.length === 0 ? (
            <Card style={{ padding:"24px", textAlign:"center" }}>
              <div style={{ color:T.textMuted, fontSize:14, marginBottom:10 }}>No jobs posted yet in your area</div>
              <div style={{ color:T.textMuted, fontSize:13 }}>Check back soon — jobs are added daily.</div>
            </Card>
          ) : nearbyJobs.map(j=>(
            <Card key={j.id} style={{ marginBottom:10 }} onClick={()=>{setSelected("job",j);setTab("c_jobs");}}>
              <div style={{ display:"flex", gap:6, marginBottom:8, flexWrap:"wrap" }}>{j.urgent&&<Badge color={T.danger}>Urgent</Badge>}<Badge color={T.blueLight}>{j.trade}</Badge></div>
              <div style={{ fontWeight:600, fontSize:14, color:T.text, marginBottom:3 }}>{j.title}</div>
              <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:T.textMuted, fontSize:12 }}>{j.loc} · {j.posted}</span><span style={{ color:T.amber, fontWeight:700 }}>{j.budget}</span></div>
            </Card>
          ))}
        </div>
      </div>
      <SH title="Recent Bids" action="View All" onAction={()=>setTab("c_bids")}/>
      {myBids.length === 0 ? (
        <Card style={{ padding:"24px", textAlign:"center" }}>
          <div style={{ color:T.textMuted, fontSize:14, marginBottom:10 }}>No bids yet</div>
          <Btn variant="subtle" sm onClick={()=>setTab("c_jobs")}>Find Work to Bid On</Btn>
        </Card>
      ) : (
        <Card style={{ padding:0 }}>
          {myBids.map((b,i)=>{
            const sc={Accepted:T.success,Pending:T.amber,Declined:T.danger};
            return <div key={i} style={{ padding:"14px 20px", borderBottom:i<myBids.length-1?`1px solid ${T.border}`:"none", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:8 }}>
              <div><div style={{ fontWeight:600, fontSize:14, color:T.text, marginBottom:3 }}>{b.job}</div><div style={{ color:T.textMuted, fontSize:13 }}>{b.client} · {b.date}</div></div>
              <div style={{ display:"flex", gap:12, alignItems:"center" }}><span style={{ fontWeight:700, color:T.text }}>{b.amount}</span><Badge color={sc[b.status]}>{b.status}</Badge></div>
            </div>;
          })}
        </Card>
      )}
    </div>
  );
}

function CJobs({ setTab, setSelected, isMobile, profile, user }) {
  const [filter,setFilter]=useState("All Trades");
  const [q,setQ]=useState("");
  const [urgent,setUrgent]=useState(false);
  const [externalJobs,setExternalJobs]=useState([]);
  const [loadingExt,setLoadingExt]=useState(false);
  const [showExternal,setShowExternal]=useState(true);
  const [radius,setRadius]=useState(50);
  const [myBidCount,setMyBidCount]=useState(0);
  useEffect(()=>{
    if(!user?.id||profile?.is_pro) return;
    const thisMonth=new Date();thisMonth.setDate(1);thisMonth.setHours(0,0,0,0);
    supabase.from("bids").select("*",{count:"exact",head:true}).eq("contractor_id",user.id).gte("created_at",thisMonth.toISOString()).then(({count})=>setMyBidCount(count||0));
  },[user?.id]);
  const [realJobs,setRealJobs]=useState([]);
  const [loadingReal,setLoadingReal]=useState(true);

  // Load real jobs from Supabase
  useEffect(()=>{
    loadRealJobs();
  },[]);

  const loadRealJobs = async() => {
    setLoadingReal(true);
    const {data,error} = await supabase
      .from("jobs")
      .select("*")
      .eq("status","open")
      .order("created_at", {ascending:false});
    if(error) console.error("Jobs load error:", error);
    if(data) setRealJobs(data);
    setLoadingReal(false);
  };

  // Fetch external jobs from Indeed via RapidAPI
  useEffect(()=>{
    fetchExternalJobs(filter==="All Trades"?"trades contractor":filter, profile?.location||'', radius);
  },[filter, radius]);

  const fetchExternalJobs = async(trade, location, radiusMiles=50) => {
    setLoadingExt(true);
    const locationQuery = ''; // Search broadly, location shown in results
    try {
      // Using JSearch API (Indeed/Glassdoor aggregator) via RapidAPI
      const response = await fetch(
        `https://jsearch.p.rapidapi.com/search?query=${encodeURIComponent((trade==="All Trades"?"contractor":trade)+" contractor")}&num_pages=1&date_posted=month${location?"&location="+encodeURIComponent(location):""}${radiusMiles?"&radius="+radiusMiles:""}`,
        {
          headers: {
            "X-RapidAPI-Key": "339ae5781fmsh684766afe1ac263p1e56dejsn46038f82179d",
            "X-RapidAPI-Host": "jsearch.p.rapidapi.com"
          }
        }
      );
      const data = await response.json();
      if (data.data && data.data.length > 0) {
        const mapped = data.data.slice(0,6).map(j=>({
          id: j.job_id,
          title: j.job_title,
          company: j.employer_name,
          loc: `${j.job_city||""}${j.job_state?", "+j.job_state:""}`,
          posted: j.job_posted_at_datetime_utc ? new Date(j.job_posted_at_datetime_utc).toLocaleDateString() : "Recently",
          budget: j.job_salary_currency ? `$${j.job_min_salary||""}${j.job_max_salary?"-$"+j.job_max_salary:""}` : "Competitive",
          desc: j.job_description?.substring(0,120)||"",
          url: j.job_apply_link,
          isExternal: true,
          trade: filter==="All Trades"?"General Contractor":filter,
        }));
        setExternalJobs(mapped);
      } else {
        setExternalJobs(getCuratedJobs(trade, location));
      }
    } catch(err) {
      // Fallback to curated external jobs if API fails
      setExternalJobs(getCuratedJobs(trade, location));
    }
    setLoadingExt(false);
  };

  // Curated fallback jobs when API not configured
  const getCuratedJobs = (trade, location) => [
    { id:"ext1", title:`${trade} Needed - Residential Project`, company:"HomeAdvisor", loc:location||"Your Area", posted:"Today", budget:"Competitive", desc:"Homeowner seeking qualified contractor for residential project. Apply through HomeAdvisor platform.", url:"https://www.homeadvisor.com/leads/", isExternal:true, trade },
    { id:"ext2", title:`Licensed ${trade} - Multiple Openings`, company:"Angi (formerly Angie's List)", loc:"Your Area", posted:"This week", budget:"Varies by job", desc:"Multiple homeowners in your area are looking for qualified contractors. Sign up to receive leads.", url:"https://www.angi.com/pro", isExternal:true, trade },
    { id:"ext3", title:`${trade} Contractor Wanted`, company:"Thumbtack", loc:"Your Area", posted:"This week", budget:"Set your own rates", desc:"Thumbtack connects you with local customers who need your services. Create a free profile.", url:"https://www.thumbtack.com/pro", isExternal:true, trade },
    { id:"ext4", title:`Subcontractor Needed - ${trade}`, company:"Craigslist", loc:"Your Area", posted:"Recently", budget:"Negotiable", desc:"General contractors in your area are looking for reliable subcontractors. Browse local listings.", url:"https://craigslist.org", isExternal:true, trade },
  ];

  const filtered=realJobs.filter(j=>(filter==="All Trades"||j.trade===filter)&&(!urgent||j.urgency==="urgent")&&(j.title?.toLowerCase().includes(q.toLowerCase())||j.location?.toLowerCase().includes(q.toLowerCase())));

  const filteredExternal = externalJobs.filter(j=>
    j.title.toLowerCase().includes(q.toLowerCase()) ||
    j.loc.toLowerCase().includes(q.toLowerCase())
  );

  const totalCount = filtered.length + (showExternal ? filteredExternal.length : 0);

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <div style={{ fontWeight:700, fontSize:isMobile?24:28, color:T.text, letterSpacing:-.5, marginBottom:4 }}>Find Work</div>
        <div style={{ color:T.textMuted, fontSize:14 }}>{totalCount} positions available</div>
      </div>

      <div style={{ display:"flex", gap:10, marginBottom:10, flexWrap:"wrap" }}>
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search jobs, trades, cities..." style={{ flex:1, minWidth:200, background:T.card, border:`1px solid ${T.border}`, borderRadius:8, padding:"10px 14px", color:T.text, fontSize:14, outline:"none" }}/>
        <button onClick={()=>setUrgent(!urgent)} style={{ background:urgent?T.dangerFaint:T.card, color:urgent?T.danger:T.textSub, border:`1px solid ${urgent?T.danger+"33":T.border}`, borderRadius:8, padding:"10px 16px", fontSize:13, fontWeight:600, cursor:"pointer" }}>Urgent only</button>
        <button onClick={()=>setShowExternal(!showExternal)} style={{ background:showExternal?T.blueFaint:T.card, color:showExternal?T.blueLight:T.textSub, border:`1px solid ${showExternal?T.blue+"44":T.border}`, borderRadius:8, padding:"10px 16px", fontSize:13, fontWeight:600, cursor:"pointer" }}>{showExternal?"Hide External":"Show External"}</button>
      </div>
      {/* Radius slider */}
      {showExternal && (
        <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8, padding:"12px 16px", marginBottom:14, display:"flex", alignItems:"center", gap:16, flexWrap:"wrap" }}>
          <span style={{ color:T.textMuted, fontSize:13, fontWeight:600, whiteSpace:"nowrap" }}>Search radius:</span>
          <div style={{ flex:1, minWidth:200, display:"flex", alignItems:"center", gap:12 }}>
            <input
              type="range"
              min="10" max="200" step="10"
              value={radius}
              onChange={e=>setRadius(Number(e.target.value))}
              style={{ flex:1, accentColor:T.blue, cursor:"pointer" }}
            />
            <span style={{ color:T.blueLight, fontWeight:700, fontSize:14, minWidth:60 }}>{radius} miles</span>
          </div>
          <div style={{ display:"flex", gap:6 }}>
            {[25,50,100,200].map(r=>(
              <button key={r} onClick={()=>setRadius(r)} style={{ background:radius===r?T.blue:T.surface, color:radius===r?"#fff":T.textSub, border:`1px solid ${radius===r?T.blue:T.border}`, borderRadius:6, padding:"4px 10px", fontSize:12, fontWeight:600, cursor:"pointer" }}>{r}mi</button>
            ))}
          </div>
        </div>
      )}

      <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:20 }}>
        {TRADES.map(t=><button key={t} onClick={()=>setFilter(t)} style={{ background:filter===t?T.blue:T.card, color:filter===t?"#fff":T.textSub, border:`1px solid ${filter===t?T.blue:T.border}`, borderRadius:6, padding:"5px 13px", fontSize:12, fontWeight:600, cursor:"pointer" }}>{t}</button>)}
      </div>

      {/* BluTrades Jobs */}
      {filtered.length > 0 && (
        <>
          <div style={{ fontWeight:700, fontSize:13, color:T.blueLight, marginBottom:12, letterSpacing:.5, textTransform:"uppercase" }}>BluTrades Jobs</div>
          <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"repeat(2,1fr)", gap:12, marginBottom:24 }}>
            {filtered.map(j=>(
              <Card key={j.id} onClick={()=>{setSelected("job",j);setTab("c_jobDetail");}}>
                <div style={{ display:"flex", gap:6, marginBottom:10, flexWrap:"wrap" }}>{j.urgency==="urgent"&&<Badge color={T.danger}>Urgent</Badge>}<Badge color={T.blueLight}>{j.trade}</Badge><Badge color={T.success}>BluTrades</Badge></div>
                <div style={{ fontWeight:600, fontSize:15, color:T.text, marginBottom:5 }}>{j.title}</div>
                <div style={{ color:T.textMuted, fontSize:13, marginBottom:12, lineHeight:1.5 }}>{(j.description||"").substring(0,85)}{(j.description||"").length>85?"...":""}</div>
                <Divider/>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div style={{ color:T.textMuted, fontSize:12 }}>{j.location} · {j.budget_display||"Open to bids"}</div>
                  <div style={{ color:T.amber, fontWeight:700 }}>{j.budget_display||"Open to bids"}</div>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* External Jobs */}
      {showExternal && (
        <>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
            <div style={{ fontWeight:700, fontSize:13, color:T.textMuted, letterSpacing:.5, textTransform:"uppercase" }}>External Opportunities</div>
            <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:5, padding:"2px 8px", fontSize:11, color:T.textMuted }}>Opens outside BluTrades</div>
            {loadingExt && <div style={{ color:T.textMuted, fontSize:12 }}>Loading…</div>}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"repeat(2,1fr)", gap:12, marginBottom:24 }}>
            {filteredExternal.map(j=>(
              <Card key={j.id} onClick={()=>window.open(j.url,"_blank")} style={{ opacity:.9 }}>
                <div style={{ display:"flex", gap:6, marginBottom:10, flexWrap:"wrap" }}>
                  <Badge color={T.textMuted}>External</Badge>
                  <Badge color={T.blueLight}>{j.trade}</Badge>
                </div>
                <div style={{ fontWeight:600, fontSize:15, color:T.text, marginBottom:3 }}>{j.title}</div>
                <div style={{ color:T.textMuted, fontSize:12, marginBottom:6 }}>via {j.company}</div>
                <div style={{ color:T.textMuted, fontSize:13, marginBottom:12, lineHeight:1.5 }}>{j.desc.substring(0,85)}{j.desc.length>85?"...":""}</div>
                <Divider/>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div style={{ color:T.textMuted, fontSize:12 }}>{j.loc} · {j.posted}</div>
                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <span style={{ color:T.amber, fontWeight:700, fontSize:13 }}>{j.budget}</span>
                    <span style={{ color:T.blueLight, fontSize:12, fontWeight:600 }}>Apply →</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Empty state */}
      {totalCount === 0 && !loadingExt && (
        <div style={{ textAlign:"center", padding:"48px 32px" }}>
          <div style={{ color:T.textMuted, fontSize:15, marginBottom:8 }}>No jobs found</div>
          <div style={{ color:T.textMuted, fontSize:13 }}>Try a different trade or search term.</div>
        </div>
      )}
    </div>
  );
}

function CJobDetail({ job, setTab, isMobile, user }) {
  const [bidOpen,setBidOpen]=useState(false);
  const [amt,setAmt]=useState("");
  const [timeline,setTimeline]=useState("");
  const [note,setNote]=useState("");
  const [submitted,setSubmitted]=useState(false);
  if (!job) return null;
  const jobBudget = job.budget_display || job.budget || "Open to bids";
  const jobLocation = job.location || job.loc || "Location not set";
  const jobDesc = job.description || job.desc || "No description provided";
  const jobTrade = job.trade || "General";
  const jobPosted = job.created_at ? new Date(job.created_at).toLocaleDateString() : "Recently";
  return (
    <div>
      <button onClick={()=>setTab("c_jobs")} style={{ background:"none", border:"none", color:T.blueLight, fontSize:14, fontWeight:600, cursor:"pointer", marginBottom:20 }}>← Back to Jobs</button>
      <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"2fr 1fr", gap:24 }}>
        <div>
          <div style={{ display:"flex", gap:6, marginBottom:12, flexWrap:"wrap" }}>{job.urgency==="urgent"&&<Badge color={T.danger}>Urgent</Badge>}<Badge color={T.blueLight}>{jobTrade}</Badge></div>
          <div style={{ fontWeight:700, fontSize:isMobile?22:28, color:T.text, letterSpacing:-.5, marginBottom:8, lineHeight:1.2 }}>{job.title}</div>
          <div style={{ color:T.amber, fontWeight:700, fontSize:20, marginBottom:6 }}>{jobBudget}</div>
          <div style={{ color:T.textMuted, fontSize:14, marginBottom:20 }}>{jobLocation} · Posted {jobPosted}</div>
          <Card style={{ marginBottom:16 }}>
            <div style={{ fontWeight:700, fontSize:15, marginBottom:10 }}>Job Description</div>
            <div style={{ color:T.textSub, fontSize:14, lineHeight:1.8, marginBottom:job.photos?.length>0?16:0 }}>{jobDesc}</div>
            {job.photos?.length>0&&(
              <>
                <div style={{ fontWeight:700, fontSize:14, marginBottom:10, marginTop:8 }}>Photos ({job.photos.length})</div>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  {job.photos.map((url,i)=>(
                    <img key={i} src={url} alt="" style={{ width:100, height:100, borderRadius:8, objectFit:"cover", border:`1px solid ${T.border}`, cursor:"pointer" }} onClick={()=>window.open(url,"_blank")}/>
                  ))}
                </div>
              </>
            )}
          </Card>
        </div>
        <div>
          <Card style={{ marginBottom:14 }}>
            <div style={{ fontWeight:700, fontSize:15, marginBottom:14 }}>About the Job</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              <div style={{ background:T.surface, borderRadius:8, padding:"10px 14px", border:`1px solid ${T.border}` }}><div style={{ color:T.textMuted, fontSize:11, marginBottom:2 }}>LOCATION</div><div style={{ color:T.text, fontSize:13, fontWeight:600 }}>{jobLocation}</div></div>
              <div style={{ background:T.surface, borderRadius:8, padding:"10px 14px", border:`1px solid ${T.border}` }}><div style={{ color:T.textMuted, fontSize:11, marginBottom:2 }}>BUDGET</div><div style={{ color:T.text, fontSize:13, fontWeight:600 }}>{jobBudget}</div></div>
            </div>
          </Card>
          {!submitted
            ? <Btn variant="primary" full onClick={()=>setBidOpen(true)} style={{ padding:"12px", fontSize:15 }}>Submit a Bid</Btn>
            : <Card style={{ textAlign:"center", padding:"24px" }}><div style={{ fontSize:28, marginBottom:8, color:T.success }}>✓</div><div style={{ fontWeight:700, color:T.success, marginBottom:6 }}>Bid Submitted</div><div style={{ color:T.textMuted, fontSize:13 }}>You'll be notified when the client responds.</div></Card>
          }
        </div>
      </div>
      <Modal open={bidOpen} onClose={()=>setBidOpen(false)} title="Submit a Bid">
        <div style={{ background:T.card, borderRadius:8, padding:"12px 14px", marginBottom:20, border:`1px solid ${T.border}` }}><div style={{ fontWeight:600, color:T.text, marginBottom:3 }}>{job.title}</div><div style={{ color:T.textMuted, fontSize:13 }}>Budget: {job.budget_display||job.budget||"Open to bids"}</div></div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <Input label="Your Bid ($)" placeholder="1,550" value={amt} onChange={e=>setAmt(e.target.value)}/>
          <Input label="Timeline" placeholder="e.g. 1 day" value={timeline} onChange={e=>setTimeline(e.target.value)}/>
        </div>
        <Input label="Message to Client" placeholder="Describe your approach and experience with this type of work." value={note} onChange={e=>setNote(e.target.value)} multiline rows={4}/>
        <div style={{ display:"flex", gap:10 }}>
          <Btn variant="ghost" onClick={()=>setBidOpen(false)} style={{ flex:1 }}>Cancel</Btn>
          <Btn variant="primary" onClick={async()=>{
                  if(!amt||!timeline) { alert("Please enter your bid amount and timeline."); return; }
                  // Check bid limit for free users
                  if(!profile?.is_pro) {
                    const thisMonth = new Date(); thisMonth.setDate(1); thisMonth.setHours(0,0,0,0);
                    const {count} = await supabase.from("bids").select("*",{count:"exact",head:true}).eq("contractor_id",user?.id).gte("created_at",thisMonth.toISOString());
                    if((count||0) >= 5) {
                      alert("Free accounts can bid on up to 5 jobs per month. Upgrade to Pro for unlimited bids!");
                      return;
                    }
                  }
                  const {error} = await supabase.from("bids").insert({
                    job_id: job.id,
                    contractor_id: user?.id,
                    amount: parseFloat(amt.replace(/[^0-9.]/g,"")),
                    timeline,
                    message: note,
                    status: "pending"
                  });
                  if(error) { alert("Failed to submit bid. Please try again."); return; }
                  // Send email to client
                  try {
                    const {data:jd} = await supabase.from("jobs").select("title, profiles:client_id(email, full_name)").eq("id", job.id).single();
                    if(jd?.profiles?.email) sendEmailNotification("new_bid", jd.profiles.email, { jobTitle: jd.title, contractorName: profile?.full_name||"A contractor", bidAmount: parseFloat(amt.replace(/[^0-9.]/g,"")).toLocaleString() });
                  } catch(e) {}
                  setSubmitted(true); setBidOpen(false);
                }} style={{ flex:2, padding:"11px" }}>Submit Bid</Btn>
        </div>
      </Modal>
    </div>
  );
}

function CTracker({ isMobile }) {
  const [jobs,setJobs]=useState(MY_ACTIVE_JOBS);
  const mark=(id,s)=>setJobs(prev=>prev.map(j=>j.id===id?{...j,status:s,pct:s==="Awaiting Payment"?100:j.pct}:j));
  const cols=[{id:"In Progress",label:"In Progress",color:T.blue},{id:"Awaiting Payment",label:"Awaiting Payment",color:T.amber},{id:"Completed",label:"Completed",color:T.success}];
  return (
    <div>
      <div style={{ marginBottom:24 }}><div style={{ fontWeight:700, fontSize:isMobile?24:28, color:T.text, letterSpacing:-.5, marginBottom:4 }}>Job Tracker</div><div style={{ color:T.textMuted, fontSize:14 }}>Track every job from start to payment</div></div>
      <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"repeat(3,1fr)", gap:16 }}>
        {cols.map(col=>(
          <div key={col.id}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14, paddingBottom:12, borderBottom:`2px solid ${col.color}33` }}>
              <div style={{ width:8, height:8, borderRadius:"50%", background:col.color }}/><span style={{ fontWeight:700, fontSize:14, color:col.color }}>{col.label}</span>
              <span style={{ marginLeft:"auto", background:`${col.color}18`, color:col.color, borderRadius:12, padding:"1px 8px", fontSize:12, fontWeight:700 }}>{jobs.filter(j=>j.status===col.id).length}</span>
            </div>
            {jobs.filter(j=>j.status===col.id).map(j=>(
              <Card key={j.id} style={{ marginBottom:10, borderLeft:`3px solid ${col.color}`, borderRadius:"0 10px 10px 0" }}>
                <div style={{ fontWeight:600, fontSize:14, color:T.text, marginBottom:4 }}>{j.title}</div>
                <div style={{ color:T.textMuted, fontSize:13, marginBottom:10 }}>{j.amount} · {j.client}</div>
                <ProgBar pct={j.pct} color={col.color} thin/>
                <div style={{ color:T.textMuted, fontSize:12, marginTop:6, marginBottom:10 }}>{j.pct}% complete</div>
                {col.id==="In Progress"&&<Btn variant="success" full sm onClick={()=>mark(j.id,"Awaiting Payment")}>Mark Complete</Btn>}
                {col.id==="Awaiting Payment"&&<Btn variant="amber" full sm onClick={()=>mark(j.id,"Completed")}>Mark Paid</Btn>}
                {col.id==="Completed"&&<div style={{ color:T.success, fontSize:12, fontWeight:600, textAlign:"center" }}>Paid & closed</div>}
              </Card>
            ))}
            {jobs.filter(j=>j.status===col.id).length===0&&<div style={{ textAlign:"center", padding:"28px 16px", color:T.textMuted, fontSize:13, border:`1px dashed ${T.border}`, borderRadius:10 }}>No jobs here</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function CBids({ setTab, isMobile, user }) {
  const [bids,setBids]=useState([]);
  const [loading,setLoading]=useState(true);

  useEffect(()=>{
    if(!user) return;
    loadBids();
  },[user?.id]);

  const loadBids = async() => {
    setLoading(true);
    const {data} = await supabase
      .from("bids")
      .select("*, jobs(title, budget_display, client_id, profiles:client_id(full_name))")
      .eq("contractor_id", user?.id)
      .order("created_at",{ascending:false});
    if(data) setBids(data);
    setLoading(false);
  };

  const sc={accepted:T.success,pending:T.amber,declined:T.danger};
  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24, flexWrap:"wrap", gap:12 }}>
        <div><div style={{ fontWeight:700, fontSize:isMobile?24:28, color:T.text, letterSpacing:-.5, marginBottom:4 }}>My Bids</div><div style={{ color:T.textMuted, fontSize:14 }}>Track your submitted bids</div></div>
        <Btn variant="primary" onClick={()=>setTab("c_jobs")}>Browse Jobs</Btn>
      </div>
      {loading ? (
        <Card style={{ padding:"32px", textAlign:"center" }}><div style={{ color:T.textMuted }}>Loading bids…</div></Card>
      ) : bids.length === 0 ? (
        <Card style={{ padding:"48px 32px", textAlign:"center" }}>
          <div style={{ color:T.textMuted, fontSize:14, marginBottom:12 }}>No bids submitted yet</div>
          <Btn variant="primary" onClick={()=>setTab("c_jobs")} sm>Browse Jobs</Btn>
        </Card>
      ) : (
        <Card style={{ padding:0 }}>
          {bids.map((b,i)=>(
            <div key={b.id} style={{ padding:"16px 20px", borderBottom:i<bids.length-1?`1px solid ${T.border}`:"none", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>
              <div>
                <div style={{ fontWeight:600, fontSize:14, color:T.text, marginBottom:4 }}>{b.jobs?.title||"Unknown Job"}</div>
                <div style={{ color:T.textMuted, fontSize:13 }}>{b.jobs?.profiles?.full_name||"Client"} · ${Number(b.amount||0).toLocaleString()}</div>
              </div>
              <div style={{ display:"flex", gap:14, alignItems:"center" }}>
                <Badge color={sc[b.status]||T.textMuted}>{b.status?.charAt(0).toUpperCase()+b.status?.slice(1)||"Pending"}</Badge>
                {b.status==="accepted"&&<Btn variant="subtle" sm>View Contract</Btn>}
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   CLIENT SCREENS
══════════════════════════════════════════════════════════════ */
function CLDashboard({ user, profile, setTab, setSelected, isMobile }) {
  const [myJobs,setMyJobs]=useState([]);
  const [totalBids,setTotalBids]=useState(0);

  useEffect(()=>{
    if(!user) return;
    loadMyJobs();
  },[user?.id]);

  const loadMyJobs = async() => {
    const {data} = await supabase
      .from("jobs")
      .select("*, bids(count)")
      .eq("client_id", user?.id)
      .order("created_at",{ascending:false});
    if(data) {
      setMyJobs(data);
      setTotalBids(data.reduce((sum,j)=>sum+(j.bids?.[0]?.count||0),0));
    }
  };
  const name = profile?.full_name?.split(" ")[0] || "there";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <div style={{ color:T.textMuted, fontSize:14, marginBottom:4 }}>{greeting}, {name}</div>
        <div style={{ fontWeight:700, fontSize:isMobile?24:30, color:T.text, letterSpacing:-.5 }}>Here's your overview</div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4,1fr)", gap:14, marginBottom:24 }}>
        {[["Active projects",String(myJobs.filter(j=>j.status==="open"||j.status==="in_progress").length),T.blue],["New bids",String(totalBids),T.amber],["Total spent","$0",T.text],["Reviews given","0",T.text]].map(([l,v,c],i)=>(
          <Card key={i} style={{ padding:"18px 20px" }}><div style={{ color:T.textMuted, fontSize:13, marginBottom:8 }}>{l}</div><div style={{ fontWeight:700, fontSize:26, color:c, letterSpacing:-.5 }}>{v}</div></Card>
        ))}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:20 }}>
        <div>
          <SH title="My Projects" action="See All" onAction={()=>setTab("cl_projects")}/>
          {myJobs.length === 0 ? (
            <Card style={{ padding:"24px", textAlign:"center" }}>
              <div style={{ color:T.textMuted, fontSize:14, marginBottom:10 }}>No projects yet</div>
              <Btn variant="primary" sm onClick={()=>setTab("cl_post")}>Post Your First Job</Btn>
            </Card>
          ) : myJobs.slice(0,3).map(j=>(
            <Card key={j.id} style={{ marginBottom:10 }} onClick={()=>{setSelected("job",j);setTab("cl_reviewBids");}}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                <div style={{ fontWeight:600, fontSize:14, color:T.text }}>{j.title}</div>
                <Badge color={j.status==="in_progress"?T.blue:j.status==="open"?T.amber:T.success}>{j.status==="in_progress"?"In Progress":j.status==="open"?"Open":"Completed"}</Badge>
              </div>
              <div style={{ color:T.textMuted, fontSize:13 }}>{j.trade} · {j.budget_display||"Open to bids"} · {j.bids?.[0]?.count||0} bids</div>
            </Card>
          ))}
        </div>
        <div>
          <SH title="Top Contractors Near You" action="Browse All" onAction={()=>setTab("cl_browse")}/>
          {CONTRACTORS.length === 0 ? (
            <Card style={{ padding:"24px", textAlign:"center" }}>
              <div style={{ color:T.textMuted, fontSize:14, marginBottom:10 }}>No contractors yet</div>
              <div style={{ color:T.textMuted, fontSize:13 }}>Verified contractors will appear here as they join BluTrades.</div>
            </Card>
          ) : CONTRACTORS.slice(0,4).map(c=>(
            <Card key={c.id} style={{ marginBottom:10, padding:"14px 18px" }} onClick={()=>{setSelected("contractor",c);setTab("cl_proDetail");}}>
              <div style={{ display:"flex", gap:12, alignItems:"center" }}>
                <div style={{ position:"relative" }}><Ini initials={c.initials} color={c.color} size={40}/><OnlineDot online={c.online}/></div>
                <div style={{ flex:1 }}><div style={{ fontWeight:600, fontSize:14, color:T.text }}>{c.name}{c.verified&&" ✓"}</div><div style={{ color:T.textMuted, fontSize:13 }}>{c.trade} · {c.loc}</div>{c.reviews>0?<Stars n={c.rating} size={11}/>:<div style={{color:T.textMuted,fontSize:11}}>New to BluTrades</div>}</div>
                <div style={{ textAlign:"right" }}><div style={{ fontWeight:700, color:T.text, fontSize:14 }}>{c.rate}</div>{c.reviews>0&&<div style={{ color:T.textMuted, fontSize:11 }}>{c.reviews} review{c.reviews!==1?"s":""}</div>}</div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

function CLPostJob({ setTab, isMobile, user }) {
  const [step,setStep]=useState(1);
  const [f,setF]=useState({title:"",trade:"Electrician",desc:"",budget:"",location:"",timeline:"",urgency:"normal",photos:[]});
  const [posted,setPosted]=useState(false);
  const set=(k,v)=>setF(p=>({...p,[k]:v}));
  if (posted) return (
    <div style={{ maxWidth:560, margin:"0 auto", textAlign:"center", paddingTop:40 }}>
      <div style={{ width:56, height:56, borderRadius:"50%", background:T.successFaint, border:`1px solid ${T.success}33`, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 20px", fontSize:22, color:T.success }}>✓</div>
      <div style={{ fontWeight:700, fontSize:24, color:T.text, marginBottom:10 }}>Job Posted Successfully</div>
      <div style={{ color:T.textSub, fontSize:15, lineHeight:1.7, marginBottom:8 }}>Your job is live. Verified contractors in your area have been notified and you should receive bids within the hour.</div>
      <div style={{ color:T.textMuted, fontSize:14, marginBottom:32 }}>You'll be notified as bids come in.</div>
      <div style={{ display:"flex", gap:12, justifyContent:"center" }}>
        <Btn variant="primary" onClick={()=>setTab("cl_home")} style={{ padding:"11px 24px" }}>Back to Dashboard</Btn>
        <Btn variant="ghost" onClick={()=>{setPosted(false);setStep(1);setF({title:"",trade:"Electrician",desc:"",budget:"",location:"",timeline:"",urgency:"normal"});}}>Post Another Job</Btn>
      </div>
    </div>
  );
  return (
    <div style={{ maxWidth:640, margin:"0 auto" }}>
      <button onClick={()=>setTab("cl_home")} style={{ background:"none", border:"none", color:T.blueLight, fontSize:14, fontWeight:600, cursor:"pointer", marginBottom:20 }}>← Back</button>
      <div style={{ fontWeight:700, fontSize:isMobile?24:28, color:T.text, letterSpacing:-.5, marginBottom:4 }}>Post a Job</div>
      <div style={{ color:T.textMuted, fontSize:14, marginBottom:24 }}>Describe your job and receive bids from verified contractors — usually within hours.</div>
      <div style={{ display:"flex", gap:8, marginBottom:24 }}>
        {["Job Details","Budget & Timeline","Review & Post"].map((s,i)=>(
          <div key={i} style={{ flex:1 }}><div style={{ height:3, borderRadius:2, background:i<step?T.blue:T.border, marginBottom:6 }}/><div style={{ fontSize:12, color:i<step?T.blueLight:T.textMuted, fontWeight:i===step-1?600:400 }}>{s}</div></div>
        ))}
      </div>
      {step===1&&<Card>
        <Input label="Job Title" placeholder="e.g. 200A Panel Upgrade, Bathroom Remodel" value={f.title} onChange={e=>set("title",e.target.value)}/>
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:12, color:"#C8DCF0", marginBottom:8, fontWeight:700, letterSpacing:.6, textTransform:"uppercase" }}>Trade Needed</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
            {TRADES_SELECT.map(t=><button key={t} onClick={()=>set("trade",t)} style={{ background:f.trade===t?T.blue:T.surface, color:f.trade===t?"#fff":T.textSub, border:`1px solid ${f.trade===t?T.blue:T.border}`, borderRadius:6, padding:"6px 13px", fontSize:12, fontWeight:600, cursor:"pointer" }}>{t}</button>)}
          </div>
        </div>
        <Input label="Description" placeholder="Describe the work needed in detail." value={f.desc} onChange={e=>set("desc",e.target.value)} multiline rows={4}/>
        <Input label="Location" placeholder="City, State" value={f.location} onChange={e=>set("location",e.target.value)}/>
        
        {/* Photo upload */}
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:12, color:"#C8DCF0", marginBottom:6, fontWeight:700, letterSpacing:.6, textTransform:"uppercase" }}>Job Photos <span style={{ color:T.textMuted, fontWeight:400, fontSize:11 }}>(optional — up to 5)</span></div>
          <div style={{ color:T.textMuted, fontSize:13, marginBottom:10 }}>Add photos so contractors know exactly what to expect.</div>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={async(e)=>{
              const files = Array.from(e.target.files).slice(0,5);
              const urls = [];
              for(const file of files) {
                const path = `job-photos/${Date.now()}_${file.name}`;
                const {error} = await supabase.storage.from("work_photos").upload(path, file);
                if(!error) {
                  const {data:{publicUrl}} = supabase.storage.from("work_photos").getPublicUrl(path);
                  urls.push(publicUrl);
                }
              }
              set("photos", urls);
            }}
            style={{ display:"none" }}
            id="job-photo-upload"
          />
          <label htmlFor="job-photo-upload" style={{ display:"flex", alignItems:"center", gap:10, background:T.surface, border:`1px dashed ${f.photos?.length>0?T.success:T.border}`, borderRadius:8, padding:"12px 16px", cursor:"pointer" }}>
            <span style={{ fontSize:20 }}>{f.photos?.length>0?"🖼":"📷"}</span>
            <span style={{ color:f.photos?.length>0?T.success:T.textMuted, fontSize:14 }}>
              {f.photos?.length>0?`${f.photos.length} photo${f.photos.length>1?"s":""} added — click to add more`:"Click to add photos"}
            </span>
          </label>
          {f.photos?.length>0&&(
            <div style={{ display:"flex", gap:8, marginTop:8, flexWrap:"wrap" }}>
              {f.photos.map((url,i)=>(
                <div key={i} style={{ width:64, height:64, borderRadius:8, overflow:"hidden", background:T.card, border:`1px solid ${T.border}`, position:"relative" }}>
                  <img src={url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                  <button onClick={()=>set("photos",f.photos.filter((_,idx)=>idx!==i))} style={{ position:"absolute", top:2, right:2, background:"#000000AA", border:"none", borderRadius:"50%", width:18, height:18, color:"#fff", fontSize:10, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <Btn variant="primary" full onClick={()=>f.title&&f.desc&&f.location&&setStep(2)} style={{ padding:"12px", fontSize:15 }}>Continue</Btn>
      </Card>}
      {step===2&&<Card>
        <Input label="Budget" placeholder="e.g. $1,000 – $2,000 or open to bids" value={f.budget} onChange={e=>set("budget",e.target.value)}/>
        <Input label="Ideal Timeline" placeholder="e.g. As soon as possible, flexible" value={f.timeline} onChange={e=>set("timeline",e.target.value)}/>
        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:12, color:"#C8DCF0", marginBottom:10, fontWeight:700, letterSpacing:.6, textTransform:"uppercase" }}>Urgency</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            {[["normal","Standard","Receive bids within 24–48 hours"],["urgent","Urgent","Emergency — need ASAP"]].map(([val,label,desc])=>(
              <div key={val} onClick={()=>set("urgency",val)} style={{ background:f.urgency===val?T.blueFaint:T.surface, border:`1px solid ${f.urgency===val?T.blue+"44":T.border}`, borderRadius:8, padding:"14px 16px", cursor:"pointer" }}>
                <div style={{ fontWeight:600, fontSize:14, color:T.text, marginBottom:3 }}>{label}</div>
                <div style={{ color:T.textMuted, fontSize:12 }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display:"flex", gap:10 }}>
          <Btn variant="ghost" onClick={()=>setStep(1)} style={{ flex:1 }}>Back</Btn>
          <Btn variant="primary" onClick={()=>setStep(3)} style={{ flex:2, padding:"11px" }}>Continue</Btn>
        </div>
      </Card>}
      {step===3&&<Card>
        <div style={{ fontWeight:700, fontSize:16, color:T.text, marginBottom:16 }}>Review Your Job Post</div>
        {[["Title",f.title||"—"],["Trade",f.trade],["Location",f.location||"—"],["Budget",f.budget||"Open to bids"],["Timeline",f.timeline||"Flexible"],["Urgency",f.urgency==="urgent"?"Urgent":"Standard"]].map(([l,v],i)=>(
          <div key={i} style={{ display:"flex", justifyContent:"space-between", paddingBottom:10, marginBottom:10, borderBottom:`1px solid ${T.border}` }}>
            <span style={{ color:T.textMuted, fontSize:14 }}>{l}</span><span style={{ fontWeight:600, fontSize:14, color:T.text, maxWidth:"60%", textAlign:"right" }}>{v}</span>
          </div>
        ))}
        <div style={{ background:T.surface, borderRadius:8, padding:"12px 14px", marginBottom:20 }}><div style={{ fontWeight:600, color:T.text, fontSize:14, marginBottom:6 }}>Description</div><div style={{ color:T.textSub, fontSize:13, lineHeight:1.6 }}>{f.desc}</div></div>
        {f.photos?.length>0&&<div style={{ marginBottom:20 }}><div style={{ fontWeight:600, color:T.text, fontSize:14, marginBottom:8 }}>Photos ({f.photos.length})</div><div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>{f.photos.map((url,i)=><img key={i} src={url} alt="" style={{ width:72, height:72, borderRadius:8, objectFit:"cover", border:`1px solid ${T.border}` }}/>)}</div></div>}
        <div style={{ display:"flex", gap:10 }}>
          <Btn variant="ghost" onClick={()=>setStep(2)} style={{ flex:1 }}>Edit</Btn>
          <Btn variant="primary" onClick={async()=>{
        // Save job to Supabase
        const {error: jobError} = await supabase.from("jobs").insert({
          title: f.title,
          trade: f.trade,
          description: f.desc,
          location: f.location,
          budget_display: f.budget,
          status: "open",
          client_id: user?.id,
          photos: f.photos||[],
        });
        if(jobError) { alert("Error posting job: " + jobError.message); return; }
        setPosted(true);
      }} style={{ flex:2, padding:"12px", fontSize:15 }}>Post Job</Btn>
        </div>
      </Card>}
    </div>
  );
}

function CLReviewBids({ job, setTab, openContract, isMobile, user, setActive }) {
  const [realBids,setRealBids]=useState([]);
  const [loadingBids,setLoadingBids]=useState(true);

  useEffect(()=>{
    if(!job?.id) return;
    loadBids();
  },[job?.id]);

  const loadBids = async() => {
    setLoadingBids(true);
    const {data,error} = await supabase
      .from("bids")
      .select("*, profiles:contractor_id(id, full_name, location)")
      .eq("job_id", job.id)
      .order("created_at", {ascending:false});
    if(!error && data) setRealBids(data);
    setLoadingBids(false);
  };

  const [acceptedConvo, setAcceptedConvo] = useState(null);

  const acceptBid = async(bid) => {
    // Update bid status
    await supabase.from("bids").update({status:"accepted"}).eq("id",bid.id);
    // Decline all other bids
    await supabase.from("bids").update({status:"declined"}).eq("job_id",job.id).neq("id",bid.id);
    // Update job status
    await supabase.from("jobs").update({status:"in_progress"}).eq("id",job.id);
    
    // Create or get conversation
    const {data:existing} = await supabase.from("conversations").select("*")
      .or(`and(participant_1.eq.${user?.id},participant_2.eq.${bid.contractor_id}),and(participant_1.eq.${bid.contractor_id},participant_2.eq.${user?.id})`)
      .maybeSingle();
    
    let convo = existing;
    if(!convo) {
      const {data:newConvo} = await supabase.from("conversations").insert({
        participant_1: user?.id,
        participant_2: bid.contractor_id,
        last_message: "Bid accepted — let's talk through the details.",
        last_message_at: new Date().toISOString()
      }).select().single();
      convo = newConvo;
    }

    // Send an automatic first message
    if(convo) {
      await supabase.from("messages").insert({
        conversation_id: convo.id,
        sender_id: user?.id,
        recipient_id: bid.contractor_id,
        content: `Hi! I accepted your bid of $${Number(bid.amount||0).toLocaleString()} for "${job.title}". Let's talk through the schedule and any details before we generate the contract.`
      });
      
      const contractorName = bid.profiles?.full_name || "Contractor";
      const initials = contractorName.split(" ").map(w=>w[0]).join("").substring(0,2).toUpperCase();
      const colors = ["#2563EB","#0D9488","#7C3AED","#B45309","#0369A1"];
      const color = colors[bid.contractor_id?.charCodeAt(0)%colors.length]||"#2563EB";
      setAcceptedConvo({...convo, other:{id:bid.contractor_id, full_name:contractorName}, initials, color});
    }
    loadBids();
  };

  const declineBid = async(bid) => {
    await supabase.from("bids").update({status:"declined"}).eq("id",bid.id);
    loadBids();
  };
  const [bidderProfile,setBidderProfile]=useState(null);
  if (!job) return null;

  const acceptedBid = realBids.find(b=>b.status==="accepted");

  return (
    <div>
      <button onClick={()=>setTab("cl_home")} style={{ background:"none", border:"none", color:T.blueLight, fontSize:14, fontWeight:600, cursor:"pointer", marginBottom:20 }}>← Back</button>
      <div style={{ fontWeight:700, fontSize:isMobile?22:28, color:T.text, letterSpacing:-.5, marginBottom:4 }}>{realBids.length} Bids Received</div>
      <div style={{ color:T.textMuted, fontSize:14, marginBottom:24 }}>{job.title} · Budget: {job.budget||"Open to bids"}</div>

      {acceptedConvo && (
        <Card style={{ marginBottom:24, border:`1px solid ${T.success}33`, background:T.successFaint }}>
          <div style={{ fontWeight:700, color:T.success, marginBottom:6, fontSize:16 }}>✓ Bid Accepted!</div>
          <div style={{ color:T.textMuted, fontSize:14, marginBottom:16 }}>A message thread has been opened. Coordinate the schedule and details before generating the contract.</div>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
            <Btn variant="primary" onClick={()=>{ if(setActive) setActive(acceptedConvo); setTab("chat"); }}>Open Message Thread</Btn>
            <Btn variant="ghost" onClick={openContract}>Generate Contract Now</Btn>
          </div>
        </Card>
      )}
      {!acceptedConvo && acceptedBid&&<Card style={{ marginBottom:24, border:`1px solid ${T.success}33`, background:T.successFaint }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12 }}>
          <div><div style={{ fontWeight:700, color:T.success, marginBottom:3 }}>Bid accepted — {acceptedBid.profiles?.full_name}</div><div style={{ color:T.textMuted, fontSize:14 }}>Message the contractor or generate a contract.</div></div>
          <Btn variant="primary" onClick={openContract}>Generate Contract</Btn>
        </div>
      </Card>}

      {loadingBids&&<Card style={{padding:"32px",textAlign:"center"}}><div style={{color:T.textMuted}}>Loading bids…</div></Card>}

      {!loadingBids&&realBids.length===0&&<Card style={{padding:"48px",textAlign:"center"}}>
        <div style={{color:T.textMuted,fontSize:15,marginBottom:8}}>No bids yet</div>
        <div style={{color:T.textMuted,fontSize:13}}>Contractors will start bidding soon. Check back shortly.</div>
      </Card>}

      <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"repeat(auto-fill,minmax(320px,1fr))", gap:14 }}>
        {realBids.map((b,i)=>{
          const isAccepted=b.status==="accepted";
          const isDeclined=b.status==="declined";
          const name=b.profiles?.full_name||"Contractor";
          const initials=name.split(" ").map(w=>w[0]).join("").substring(0,2).toUpperCase();
          const colors=["#2563EB","#0D9488","#7C3AED","#B45309","#0369A1"];
          const color=colors[b.contractor_id?.charCodeAt(0)%colors.length]||T.blue;
          return (
            <Card key={b.id} style={{ border:`1px solid ${isAccepted?T.success+"44":T.border}`, opacity:isDeclined?0.65:1 }}>
              <div style={{ display:"flex", gap:14, alignItems:"flex-start", marginBottom:14 }}>
                <Ini initials={initials} color={color} size={48}/>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:15, color:T.text, marginBottom:4 }}>{name}</div>
                  <div style={{ color:T.textMuted, fontSize:13 }}>{b.profiles?.location||"Location not set"}</div>
                  <div style={{ color:T.textMuted, fontSize:13, marginTop:2 }}>Timeline: {b.timeline||"Not specified"}</div>
                </div>
                <div style={{ textAlign:"right" }}><div style={{ fontWeight:700, fontSize:22, color:T.amber }}>${Number(b.amount||0).toLocaleString()}</div></div>
              </div>
              {b.message&&<div style={{ background:T.surface, borderRadius:8, padding:"12px 14px", marginBottom:14, border:`1px solid ${T.border}` }}><div style={{ color:T.textSub, fontSize:13, lineHeight:1.6 }}>"{b.message}"</div></div>}
              {!isAccepted&&!isDeclined
                ? <div style={{ display:"flex", gap:8 }}>
                    <Btn variant="ghost" onClick={()=>declineBid(b)} style={{ flex:1 }}>Decline</Btn>
                    <Btn variant="primary" onClick={()=>acceptBid(b)} style={{ flex:2 }}>Accept Bid</Btn>
                  </div>
                : isAccepted
                  ? <div>
                      <div style={{ background:T.successFaint, border:`1px solid ${T.success}33`, borderRadius:8, padding:"10px", textAlign:"center", color:T.success, fontWeight:600, fontSize:13, marginBottom:8 }}>✓ Accepted</div>
                    </div>
                  : <div style={{ background:T.surface, borderRadius:8, padding:"10px", textAlign:"center", color:T.textMuted, fontSize:13 }}>Declined</div>
              }
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function CLBrowse({ setTab, setSelected, isMobile }) {
  const [filter,setFilter]=useState("All Trades");
  const [q,setQ]=useState("");
  const [verifiedOnly,setVerifiedOnly]=useState(false);
  const [contractors,setContractors]=useState([]);
  const [loading,setLoading]=useState(true);

  useEffect(()=>{
    loadContractors();
  },[]);

  const loadContractors = async() => {
    setLoading(true);
    const {data,error} = await supabase
      .from("profiles")
      .select("*, contractor_profiles(trade, bio)")
      .eq("role","contractor")
      .order("is_pro", {ascending:false});
    if(!error && data) {
      const ids = data.map(p=>p.id);
      const {data:reviewData} = await supabase
        .from("reviews")
        .select("contractor_id, rating, content")
        .in("contractor_id", ids);

      const mapped = data.map(p=>{
        const myReviews = reviewData?.filter(r=>r.contractor_id===p.id)||[];
        const avgRating = myReviews.length>0
          ? Math.round((myReviews.reduce((s,r)=>s+r.rating,0)/myReviews.length)*10)/10
          : 0;
        return {
          id: p.id,
          name: p.full_name||"Unknown",
          trade: p.contractor_profiles?.trade||"General Contractor",
          loc: p.location||"Location not set",
          initials: (p.full_name||"??").split(" ").map(w=>w[0]).join("").substring(0,2).toUpperCase(),
          color: ["#2563EB","#0D9488","#7C3AED","#B45309","#0369A1","#854D0E"][p.id.charCodeAt(0)%6],
          bio: p.contractor_profiles?.bio||"",
          is_pro: p.is_pro||false,
          verified: false,
          insured: false,
          licensed: false,
          online: false,
          rating: avgRating,
          reviews: myReviews.length,
          reviewList: myReviews.map(r=>({name:"Client",stars:r.rating,text:r.content||"",role:"Client"})),
          jobs: 0,
          rate: "Contact for rate",
          yrs: 0,
          skills: [],
          photos: 0,
        };
      });
      setContractors(mapped);
    }
    setLoading(false);
  };

  const filtered=contractors.filter(c=>(filter==="All Trades"||c.trade===filter)&&(!verifiedOnly||c.verified)&&(c.name.toLowerCase().includes(q.toLowerCase())||c.trade.toLowerCase().includes(q.toLowerCase())));
  return (
    <div>
      <div style={{ marginBottom:24 }}><div style={{ fontWeight:700, fontSize:isMobile?24:28, color:T.text, letterSpacing:-.5, marginBottom:4 }}>Find a Contractor</div><div style={{ color:T.textMuted, fontSize:14 }}>{filtered.length} verified professionals</div></div>
      <div style={{ display:"flex", gap:10, marginBottom:14, flexWrap:"wrap" }}>
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search by name, trade, or city..." style={{ flex:1, minWidth:200, background:T.card, border:`1px solid ${T.border}`, borderRadius:8, padding:"10px 14px", color:T.text, fontSize:14, outline:"none" }}/>
        <button onClick={()=>setVerifiedOnly(!verifiedOnly)} style={{ background:verifiedOnly?T.blueFaint:T.card, color:verifiedOnly?T.blueLight:T.textSub, border:`1px solid ${verifiedOnly?T.blue+"44":T.border}`, borderRadius:8, padding:"10px 16px", fontSize:13, fontWeight:600, cursor:"pointer" }}>Verified Only</button>
      </div>
      <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:20 }}>
        {TRADES.map(t=><button key={t} onClick={()=>setFilter(t)} style={{ background:filter===t?T.blue:T.card, color:filter===t?"#fff":T.textSub, border:`1px solid ${filter===t?T.blue:T.border}`, borderRadius:6, padding:"5px 13px", fontSize:12, fontWeight:600, cursor:"pointer" }}>{t}</button>)}
      </div>
      {loading && <Card style={{ padding:"32px", textAlign:"center", marginBottom:16 }}><div style={{ color:T.textMuted }}>Loading contractors…</div></Card>}
      <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"repeat(2,1fr)", gap:12 }}>
        {!loading && filtered.length === 0 && (
          <div style={{ gridColumn:"1/-1", textAlign:"center", padding:"48px 32px" }}>
            <div style={{ color:T.textMuted, fontSize:15, marginBottom:8 }}>No contractors found</div>
            <div style={{ color:T.textMuted, fontSize:13 }}>Try a different trade or search term. More contractors are joining daily.</div>
          </div>
        )}
        {filtered.map(c=>(
          <Card key={c.id} onClick={()=>{setSelected("contractor",c);setTab("cl_proDetail");}}>
            <div style={{ display:"flex", gap:14, alignItems:"flex-start" }}>
              <div style={{ position:"relative" }}><Ini initials={c.initials} color={c.color} size={52}/><OnlineDot online={c.online}/></div>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:3, flexWrap:"wrap" }}><span style={{ fontWeight:700, fontSize:16, color:T.text }}>{c.name}</span>{c.verified&&<span style={{ color:T.success, fontSize:12 }}>✓</span>}{c.is_pro&&<span style={{ background:"linear-gradient(135deg,#7C3AED,#2563EB)", borderRadius:4, padding:"1px 6px", color:"#fff", fontSize:10, fontWeight:700 }}>⭐ PRO</span>}</div>
                <div style={{ color:T.textMuted, fontSize:13, marginBottom:6 }}>{c.trade} · {c.loc}</div>
                {c.reviews>0?<Stars n={c.rating}/>:<div style={{color:T.textMuted,fontSize:12}}>New to BluTrades</div>}
                <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginTop:8 }}>
                  {c.insured&&<Badge color={T.success}>Insured</Badge>}
                  {c.licensed&&<Badge color={T.blueLight}>Licensed</Badge>}
                  {c.online&&<Badge color="#10B981">Available</Badge>}
                </div>
              </div>
              <div style={{ textAlign:"right", flexShrink:0 }}><div style={{ fontWeight:700, color:T.text, fontSize:14 }}>{c.rate}</div>{c.reviews>0&&<div style={{ color:T.textMuted, fontSize:12, marginTop:4 }}>{c.reviews} review{c.reviews!==1?"s":""}</div>}</div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function CLProDetail({ c, setTab, openContract, openVouch, user, setActive, isMobile }) {
  const [photoIdx,setPhotoIdx]=useState(0);
  if (!c) return null;
  const photoColors=["#2563EB","#0D9488","#7C3AED","#B45309","#854D0E","#0369A1"];
  return (
    <div>
      <button onClick={()=>setTab("cl_browse")} style={{ background:"none", border:"none", color:T.blueLight, fontSize:14, fontWeight:600, cursor:"pointer", marginBottom:20 }}>← Back to Contractors</button>
      <Card style={{ marginBottom:20 }}>
        <div style={{ display:"flex", gap:20, alignItems:"flex-start", flexWrap:isMobile?"wrap":"nowrap" }}>
          <div style={{ position:"relative", flexShrink:0 }}><Ini initials={c.initials} color={c.color} size={isMobile?64:80}/><OnlineDot online={c.online}/></div>
          <div style={{ flex:1 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginBottom:6 }}>
              <span style={{ fontWeight:700, fontSize:isMobile?22:28, color:T.text, letterSpacing:-.5 }}>{c.name}</span>
              {c.verified&&<Badge color={T.success}>Identity Verified</Badge>}
            </div>
            <div style={{ color:T.textMuted, fontSize:15, marginBottom:8 }}>{c.trade} · {c.loc}</div>
            {c.reviews>0&&<Stars n={c.rating} size={15}/>}
            {c.reviews===0&&<div style={{color:T.textMuted,fontSize:13}}>New to BluTrades</div>}
            <div style={{ fontWeight:700, fontSize:20, color:T.text, marginTop:8 }}>{c.rate}</div>
          </div>
          <div style={{ display:"flex", gap:8, flexShrink:0, flexWrap:"wrap" }}>
            <Btn variant="primary" onClick={openContract}>Hire & Contract</Btn>
            <Btn variant="ghost" onClick={async()=>{
              if(!user){alert("Please sign in to message contractors.");return;}
              // Create or get conversation
              const {data:existing}=await supabase.from("conversations").select("*").or(`and(participant_1.eq.${user.id},participant_2.eq.${c.id}),and(participant_1.eq.${c.id},participant_2.eq.${user.id})`).single();
              let convo=existing;
              if(!convo){
                const{data:newConvo}=await supabase.from("conversations").insert({participant_1:user.id,participant_2:c.id}).select().single();
                convo=newConvo;
              }
              if(convo){
                const colors=["#2563EB","#0D9488","#7C3AED","#B45309","#0369A1","#854D0E"];
                const color=colors[c.id%colors.length]||T.blue;
                if(setActive) setActive({...convo,other:{id:c.id,full_name:c.name},initials:c.initials,color});
                setTab("chat");
              }
            }}>Message</Btn>
            <Btn variant="ghost" onClick={openVouch}>Leave Vouch</Btn>
          </div>
        </div>
      </Card>
      <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:20 }}>
        <div>
          <Card style={{ marginBottom:16 }}>
            <div style={{ fontWeight:700, fontSize:15, marginBottom:14 }}>Work Photos</div>
            {(!c.photos||c.photos===0)?<div style={{ background:T.surface, borderRadius:8, padding:"24px", textAlign:"center", color:T.textMuted, fontSize:14, border:`1px solid ${T.border}`, marginBottom:10 }}>No photos uploaded yet</div>:
            <div style={{ background:`linear-gradient(135deg,${photoColors[photoIdx%photoColors.length]}33,${T.card})`, borderRadius:8, height:200, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:10, position:"relative", border:`1px solid ${T.border}` }}>
              <div style={{ textAlign:"center", color:T.textMuted }}><div style={{ fontSize:36, marginBottom:8, color:photoColors[photoIdx%photoColors.length], opacity:.5 }}>◼</div><div style={{ fontSize:13 }}>Photo {photoIdx+1} of {c.photos}</div><div style={{ fontSize:11, marginTop:4 }}>{photoIdx%2===0?"Before":"After"}</div></div>
              <div style={{ position:"absolute", bottom:10, right:10, display:"flex", gap:6 }}>
                <button onClick={()=>setPhotoIdx(i=>Math.max(0,i-1))} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:6, width:28, height:28, color:T.text, cursor:"pointer", fontSize:14 }}>‹</button>
                <button onClick={()=>setPhotoIdx(i=>Math.min(c.photos-1,i+1))} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:6, width:28, height:28, color:T.text, cursor:"pointer", fontSize:14 }}>›</button>
              </div>
            </div>}
            <div style={{ display:"flex", gap:6 }}>
              {Array.from({length:Math.min(c.photos,5)}).map((_,i)=><div key={i} onClick={()=>setPhotoIdx(i)} style={{ flex:1, height:44, borderRadius:6, background:T.surface, border:`1.5px solid ${i===photoIdx?c.color:T.border}`, cursor:"pointer" }}/>)}
            </div>
          </Card>
          <Card style={{ marginBottom:16 }}>
            <div style={{ fontWeight:700, fontSize:15, marginBottom:14 }}>Credentials</div>
            {[["Identity Verified",c.verified,c.verified?"Verified":"Not verified"],["Workers' Comp",c.insured,c.insured?`Active · Exp ${c.expiry}`:"Not on file"],["State License",c.licensed,c.licensed?"Licensed & Active":"Unlicensed"]].map(([lbl,ok,txt],i,arr)=>(
              <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", paddingBottom:i<arr.length-1?12:0, marginBottom:i<arr.length-1?12:0, borderBottom:i<arr.length-1?`1px solid ${T.border}`:"none" }}>
                <span style={{ color:T.textSub, fontSize:14 }}>{lbl}</span>
                <Badge color={ok?T.success:i===2?T.amber:T.danger}>{txt}</Badge>
              </div>
            ))}
          </Card>
          <Card>
            <div style={{ fontWeight:700, fontSize:15, marginBottom:10 }}>About</div>
            <div style={{ color:T.textSub, fontSize:14, lineHeight:1.8, marginBottom:16 }}>{c.bio}</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
              {c.skills.map((s,i)=><span key={i} style={{ background:T.surface, color:T.textSub, border:`1px solid ${T.border}`, borderRadius:6, padding:"4px 12px", fontSize:13 }}>{s}</span>)}
            </div>
          </Card>
        </div>
        <div>
          <Card>
            <SH title="Reviews & Vouches" sub={`${c.reviews} total`}/>
            {(c.reviewList||[]).map((r,i)=>(
              <div key={i} style={{ paddingBottom:i<c.reviewList.length-1?18:0, marginBottom:i<c.reviewList.length-1?18:0, borderBottom:i<c.reviewList.length-1?`1px solid ${T.border}`:"none" }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                  <div><span style={{ fontWeight:600, color:T.text }}>{r.name}</span><span style={{ color:T.textMuted, fontSize:12, marginLeft:8 }}>{r.role}</span></div>
                  <div style={{ textAlign:"right" }}><Stars n={r.stars} size={12}/><div style={{ color:T.textMuted, fontSize:11, marginTop:2 }}>{r.date}</div></div>
                </div>
                <div style={{ color:T.textSub, fontSize:14, lineHeight:1.6 }}>"{r.text}"</div>
              </div>
            ))}
            <Divider/>
            <div style={{ background:T.surface, borderRadius:8, padding:"14px 16px", border:`1px solid ${T.border}` }}>
              <div style={{ fontWeight:600, color:T.text, marginBottom:4, fontSize:14 }}>BluTrades Guarantee</div>
              <div style={{ color:T.textMuted, fontSize:13, lineHeight:1.6 }}>All payments through BluTrades are protected. If work isn't completed as agreed, you're covered.</div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function CLProjects({ setTab, isMobile, user }) {
  const [projects,setProjects]=useState([]);
  const [loading,setLoading]=useState(true);
  const [reviewJob,setReviewJob]=useState(null);
  const [reviewContractorId,setReviewContractorId]=useState(null);

  useEffect(()=>{
    if(!user) return;
    loadProjects();
  },[user?.id]);

  const loadProjects = async() => {
    setLoading(true);
    const {data} = await supabase
      .from("jobs")
      .select("*, bids(contractor_id, status)")
      .eq("client_id", user?.id)
      .order("created_at",{ascending:false});
    if(data) setProjects(data);
    setLoading(false);
  };
  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24, flexWrap:"wrap", gap:12 }}>
        <div><div style={{ fontWeight:700, fontSize:isMobile?24:28, color:T.text, letterSpacing:-.5, marginBottom:4 }}>My Projects</div><div style={{ color:T.textMuted, fontSize:14 }}>Track every job from posting to completion</div></div>
        <Btn variant="teal" onClick={()=>setTab("cl_post")}>Post New Job</Btn>
      </div>
      {loading&&<Card style={{padding:"32px",textAlign:"center",marginBottom:20}}><div style={{color:T.textMuted}}>Loading projects…</div></Card>}
      {!loading&&projects.length===0&&<Card style={{padding:"48px",textAlign:"center",marginBottom:20}}>
        <div style={{color:T.textMuted,fontSize:15,marginBottom:12}}>No projects yet</div>
        <Btn variant="primary" sm onClick={()=>setTab("cl_post")}>Post Your First Job</Btn>
      </Card>}
      <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"repeat(auto-fill,minmax(340px,1fr))", gap:14, marginBottom:28 }}>
        {projects.map(p=>{
          const statusLabel = p.status==="in_progress"?"In Progress":p.status==="open"?"Seeking Bids":p.status==="completed"?"Completed":"Open";
          const statusColor = p.status==="in_progress"?T.blue:p.status==="open"?T.amber:T.success;
          const bidCount = p.bids?.length||0;
          return (
            <Card key={p.id} style={{ borderLeft:`3px solid ${statusColor}`, borderRadius:"0 10px 10px 0" }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                <div style={{ fontWeight:700, fontSize:15, color:T.text }}>{p.title}</div>
                <Badge color={statusColor}>{statusLabel}</Badge>
              </div>
              <div style={{ color:T.textMuted, fontSize:13, marginBottom:12 }}>{p.trade} · {p.budget_display||"Open to bids"} · {bidCount} bid{bidCount!==1?"s":""}</div>
              {p.status==="open"&&<Btn variant="subtle" full sm onClick={()=>{setTab("cl_reviewBids");}}>Review {bidCount} Bids</Btn>}
              {p.status==="in_progress"&&<div style={{ display:"flex", gap:8, flexDirection:"column" }}>
                <div style={{ display:"flex", gap:8 }}>
                  <Btn variant="ghost" style={{ flex:1 }} sm onClick={()=>setTab("messages")}>Message</Btn>
                  <Btn variant="subtle" style={{ flex:1 }} sm onClick={()=>setTab("contracts")}>Contract</Btn>
                </div>
                <Btn variant="primary" full sm onClick={()=>{
                  const acceptedBid = p.bids?.find?.(b=>b.status==="accepted");
                  setReviewContractorId(acceptedBid?.contractor_id||p.bids?.[0]?.contractor_id||null);
                  setReviewJob(p);
                  console.log("Bids:", p.bids, "Accepted:", acceptedBid);
                }}>Mark Complete & Leave Review</Btn>
              </div>}
              {p.status==="completed"&&<div style={{ color:T.success, fontSize:13, fontWeight:600, textAlign:"center" }}>✓ Completed</div>}
            </Card>
          );
        })}
      </div>

    <ReviewModal
      open={!!reviewJob}
      onClose={()=>{ setReviewJob(null); setReviewContractorId(null); loadProjects(); }}
      job={reviewJob}
      contractorId={reviewContractorId}
      clientId={user?.id}
    />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   SHARED SCREENS
══════════════════════════════════════════════════════════════ */
function ContractsPage({ openContract, isMobile }) {
  const sc={"Awaiting Signature":T.amber,"In Progress":T.blue,"Completed":T.success};
  const fileRef = useRef(null);
  const [importMode,setImportMode]=useState(false);
  const [importFile,setImportFile]=useState(null);
  const [importText,setImportText]=useState("");
  const [importUploaded,setImportUploaded]=useState(false);
  const [clientName,setClientName]=useState("");
  const [clientEmail,setClientEmail]=useState("");
  const [contractAmt,setContractAmt]=useState("");
  const [sent,setSent]=useState(false);

  if (importMode) return (
    <div style={{ maxWidth:640, margin:"0 auto" }}>
      <button onClick={()=>{setImportMode(false);setImportFile(null);setImportText("");setImportUploaded(false);setSent(false);}} style={{ background:"none", border:"none", color:T.blueLight, fontSize:14, fontWeight:600, cursor:"pointer", marginBottom:20 }}>← Back to Contracts</button>
      <div style={{ fontWeight:700, fontSize:isMobile?24:28, color:T.text, letterSpacing:-.5, marginBottom:4 }}>Import a Contract</div>
      <div style={{ color:T.textMuted, fontSize:14, marginBottom:24 }}>Upload your own contract to send for e-signature.</div>
      {!sent?(!importUploaded?<Card>
        <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" style={{ display:"none" }} onChange={e=>e.target.files[0]&&setImportFile(e.target.files[0])}/>
        <div onClick={()=>fileRef.current?.click()} style={{ border:`2px dashed ${importFile?T.blue:T.border}`, borderRadius:10, padding:"32px 20px", textAlign:"center", cursor:"pointer", marginBottom:16, background:importFile?T.blueFaint:"transparent" }}>
          {importFile?<div><div style={{ fontSize:32, marginBottom:8 }}>📄</div><div style={{ fontWeight:600, color:T.text, marginBottom:4 }}>{importFile.name}</div><div style={{ color:T.textMuted, fontSize:13 }}>Click to change</div></div>:<div><div style={{ fontSize:36, marginBottom:10, opacity:.4 }}>⬆</div><div style={{ fontWeight:600, color:T.text, marginBottom:4 }}>Click to upload your contract</div><div style={{ color:T.textMuted, fontSize:13 }}>PDF or Word document</div></div>}
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}><div style={{ flex:1, height:1, background:T.border }}/><span style={{ color:T.textMuted, fontSize:13 }}>or paste contract text</span><div style={{ flex:1, height:1, background:T.border }}/></div>
        <Input placeholder="Paste your contract text here..." value={importText} onChange={e=>setImportText(e.target.value)} multiline rows={5}/>
        <div style={{ display:"flex", gap:10, marginTop:8 }}>
          <Btn variant="ghost" onClick={()=>setImportMode(false)} style={{ flex:1 }}>Cancel</Btn>
          <Btn variant="primary" onClick={()=>(importFile||importText.trim())&&setImportUploaded(true)} style={{ flex:2, padding:"12px" }}>Continue</Btn>
        </div>
      </Card>:<Card>
        <div style={{ background:T.surface, borderRadius:8, padding:"12px 14px", marginBottom:20, border:`1px solid ${T.border}`, display:"flex", gap:10, alignItems:"center" }}>
          <span style={{ fontSize:20 }}>📄</span>
          <div><div style={{ fontWeight:600, color:T.text }}>{importFile?importFile.name:"Pasted Contract"}</div><div style={{ color:T.textMuted, fontSize:12 }}>Contract ready to send</div></div>
        </div>
        <Input label="Client Name" placeholder="Full legal name" value={clientName} onChange={e=>setClientName(e.target.value)}/>
        <Input label="Client Email" placeholder="client@email.com" value={clientEmail} onChange={e=>setClientEmail(e.target.value)} type="email"/>
        <Input label="Contract Amount ($)" placeholder="1,550" value={contractAmt} onChange={e=>setContractAmt(e.target.value)}/>
        <div style={{ display:"flex", gap:10, marginTop:8 }}>
          <Btn variant="ghost" onClick={()=>setImportUploaded(false)} style={{ flex:1 }}>Back</Btn>
          <Btn variant="primary" onClick={()=>clientName&&setSent(true)} style={{ flex:2, padding:"12px" }}>Send for Signature</Btn>
        </div>
      </Card>):(
        <Card style={{ textAlign:"center", padding:"40px" }}>
          <div style={{ width:48, height:48, borderRadius:"50%", background:T.successFaint, border:`1px solid ${T.success}33`, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px", fontSize:20, color:T.success }}>✓</div>
          <div style={{ fontWeight:700, fontSize:20, color:T.text, marginBottom:8 }}>Contract Sent</div>
          <div style={{ color:T.textMuted, fontSize:14, marginBottom:24 }}>Sent to {clientName} for signature. Both parties receive a signed PDF.</div>
          <Btn variant="primary" onClick={()=>{setImportMode(false);setImportFile(null);setImportText("");setImportUploaded(false);setSent(false);}}>Done</Btn>
        </Card>
      )}
    </div>
  );

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24, flexWrap:"wrap", gap:12 }}>
        <div><div style={{ fontWeight:700, fontSize:isMobile?24:28, color:T.text, letterSpacing:-.5, marginBottom:4 }}>Contracts</div><div style={{ color:T.textMuted, fontSize:14 }}>All your legally binding agreements</div></div>
        <div style={{ display:"flex", gap:10 }}>
          <Btn variant="ghost" onClick={()=>setImportMode(true)}>Import Contract</Btn>
          <Btn variant="primary" onClick={openContract}>Generate Contract</Btn>
        </div>
      </div>
      {MY_CONTRACTS.length === 0 ? (
        <Card style={{ padding:"48px 32px", textAlign:"center", marginBottom:28 }}>
          <div style={{ color:T.textMuted, fontSize:14, marginBottom:12 }}>No contracts yet</div>
          <div style={{ color:T.textMuted, fontSize:13, marginBottom:16 }}>Generate a contract after accepting a bid or import an existing one.</div>
          <Btn variant="primary" onClick={openContract} sm>Generate Your First Contract</Btn>
        </Card>
      ) : (
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))", gap:12, marginBottom:28 }}>
        {MY_CONTRACTS.map((c,i)=>(
          <Card key={i}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}><div style={{ fontWeight:700, fontSize:15, color:T.text, flex:1, marginRight:12 }}>{c.title}</div><StatusDot status={c.status}/></div>
            <div style={{ color:T.textMuted, fontSize:13, marginBottom:12 }}>{c.client} · {c.date}</div>
            <Divider/>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span style={{ fontWeight:700, color:T.text, fontSize:16 }}>{c.amount}</span>
              {c.status==="Awaiting Signature"?<Btn variant="primary" sm>Review & Sign</Btn>:<span style={{ color:T.success, fontSize:13, fontWeight:600 }}>Signed</span>}
            </div>
          </Card>
        ))}
      </div>
      )}
      <SH title="Contract Templates"/>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))", gap:10 }}>
        {[["Standard Service Agreement","All Trades"],["Electrical Work Contract","Electricians"],["Plumbing Service Contract","Plumbers"],["Roofing Contract","Roofers"],["HVAC Installation","HVAC"],["General Contractor Agreement","GC"]].map(([name,trade],i)=>(
          <Card key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }} onClick={openContract}>
            <div><div style={{ fontWeight:600, fontSize:14, color:T.text }}>{name}</div><div style={{ color:T.textMuted, fontSize:12, marginTop:3 }}>{trade}</div></div>
            <span style={{ color:T.textMuted, fontSize:18 }}>›</span>
          </Card>
        ))}
      </div>
    </div>
  );
}

function MessagesPage({ setTab, setActive, user, isMobile, onRead }) {
  const [convos, setConvos] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasLoaded = useRef(false);
  const [newMsgOpen, setNewMsgOpen] = useState(false);
  const [searchEmail, setSearchEmail] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");

  const startConversation = async () => {
    if (!searchEmail.trim()) return;
    setSearching(true);
    setSearchError("");
    try {
      // Find user by email
      const { data: found, error } = await supabase
        .from("profiles")
        .select("id, full_name")
        .eq("email", searchEmail.trim().toLowerCase())
        .single();

      if (error || !found) {
        setSearchError("No BluTrades account found with that email.");
        setSearching(false);
        return;
      }

      if (found.id === user.id) {
        setSearchError("You can't message yourself!");
        setSearching(false);
        return;
      }

      // Create or get existing conversation
      const { data: existing } = await supabase
        .from("conversations")
        .select("*")
        .or(`and(participant_1.eq.${user.id},participant_2.eq.${found.id}),and(participant_1.eq.${found.id},participant_2.eq.${user.id})`)
        .single();

      let convo = existing;
      if (!convo) {
        const { data: newConvo } = await supabase
          .from("conversations")
          .insert({ participant_1: user.id, participant_2: found.id })
          .select()
          .single();
        convo = newConvo;
      }

      if (convo) {
        const colors = ["#2563EB","#0D9488","#7C3AED","#B45309","#0369A1","#854D0E"];
        const color = colors[found.id.charCodeAt(0) % colors.length];
        const initials = found.full_name?.split(" ").map(w=>w[0]).join("").substring(0,2).toUpperCase() || "??";
        setActive({ ...convo, other: found, initials, color });
        setNewMsgOpen(false);
        setSearchEmail("");
        setTab("chat");
      }
    } catch(err) {
      setSearchError("Something went wrong. Try again.");
    }
    setSearching(false);
  };

  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!user) return;
    loadConversations();
    loadUnreadCount();

    // Realtime for conversations
    const sub1 = supabase
      .channel("convos_" + user.id + "_" + Date.now())
      .on("postgres_changes", { event:"*", schema:"public", table:"conversations" }, () => {
        loadConversations(true);
      })
      .subscribe();

    // Realtime for new messages (to update unread badge)
    const sub2 = supabase
      .channel("msgs_unread_" + user.id + "_" + Date.now())
      .on("postgres_changes", {
        event:"INSERT", schema:"public", table:"messages",
        filter:`recipient_id=eq.${user.id}`
      }, () => {
        loadConversations(true);
        loadUnreadCount();
      })
      .subscribe();

    // Poll every 30 seconds as backup (not 2s - causes glitching)
    const poll = setInterval(() => {
      loadConversations(true);
      loadUnreadCount();
    }, 30000);

    return () => {
      supabase.removeChannel(sub1);
      supabase.removeChannel(sub2);
      clearInterval(poll);
    };
  }, [user?.id]);

  const loadUnreadCount = async () => {
    if (!user) return;
    const { count } = await supabase
      .from("messages")
      .select("*", { count:"exact", head:true })
      .eq("recipient_id", user.id)
      .eq("read", false);
    setUnreadCount(count || 0);
  };

  const [profiles, setProfiles] = useState({});

  const loadConversations = async (silent=false) => {
    if (!user) return;
    if(!silent && !hasLoaded.current) setLoading(true);
    try {
      const { data, error } = await supabase
        .from("conversations")
        .select("*")
        .or(`participant_1.eq.${user.id},participant_2.eq.${user.id}`)
        .order("last_message_at", { ascending: false });

      if (!error && data) {
        setConvos(data);
        hasLoaded.current = true;
        if(data.length > 0) {
          const otherIds = [...new Set(data.map(c => c.participant_1 === user.id ? c.participant_2 : c.participant_1).filter(Boolean))];
          const { data: pd } = await supabase.from("profiles").select("id, full_name").in("id", otherIds);
          if(pd) { const map = {}; pd.forEach(p => { map[p.id] = p; }); setProfiles(map); }
        }
        if(onRead) onRead();
      }
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const getOther = (convo) => {
    if (!user) return null;
    const otherId = convo.participant_1 === user.id ? convo.participant_2 : convo.participant_1;
    return profiles[otherId] || { full_name: "User", id: otherId };
  };

  const getInitials = (name) => name?.split(" ").map(w=>w[0]).join("").substring(0,2).toUpperCase() || "??";

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
        <div style={{ fontWeight:700, fontSize:isMobile?24:28, color:T.text, letterSpacing:-.5 }}>Messages</div>
        <Btn variant="primary" sm onClick={()=>setNewMsgOpen(true)}>+ New Message</Btn>
      </div>
      <div style={{ color:T.textMuted, fontSize:14, marginBottom:24 }}>Your conversations</div>
      <div style={{ maxWidth:640 }}>
        {loading ? (
          <Card style={{ padding:"32px", textAlign:"center" }}>
            <div style={{ color:T.textMuted, fontSize:14 }}>Loading conversations…</div>
          </Card>
        ) : convos.length === 0 ? (
          <Card style={{ padding:"48px 32px", textAlign:"center" }}>
            <div style={{ color:T.textMuted, fontSize:14, marginBottom:8 }}>No messages yet</div>
            <div style={{ color:T.textMuted, fontSize:13 }}>When you connect with clients or contractors, your conversations will appear here.</div>
          </Card>
        ) : (
          <Card style={{ padding:0 }}>
            {convos.map((convo, i) => {
              const other = getOther(convo);
              if (!other) return null;
              const initials = getInitials(other.full_name);
              const colors = ["#2563EB","#0D9488","#7C3AED","#B45309","#0369A1","#854D0E"];
              const color = colors[other.id?.charCodeAt(0) % colors.length] || T.blue;
              const timeAgo = convo.last_message_at ? new Date(convo.last_message_at).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"}) : "";
              return (
                <div key={convo.id} onClick={()=>{ setActive({...convo, other, initials, color}); setTab("chat"); }}
                  style={{ padding:"16px 20px", borderBottom:i<convos.length-1?`1px solid ${T.border}`:"none", display:"flex", gap:14, alignItems:"center", cursor:"pointer" }}>
                  <Ini initials={initials} color={color} size={44}/>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                      <span style={{ fontWeight:600, fontSize:14, color:T.text }}>{other.full_name}</span>
                      <span style={{ color:T.textMuted, fontSize:12 }}>{timeAgo}</span>
                    </div>
                    <div style={{ color:T.textMuted, fontSize:13, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{convo.last_message||"Start a conversation"}</div>
                  </div>
                </div>
              );
            })}
          </Card>
        )}
      </div>

      {/* New Message Modal */}
      <Modal open={newMsgOpen} onClose={()=>{ setNewMsgOpen(false); setSearchEmail(""); setSearchError(""); }} title="New Message">
        <p style={{ color:T.textSub, fontSize:14, marginBottom:20 }}>Enter the email address of the person you want to message.</p>
        <Input
          label="Their Email Address"
          placeholder="contractor@email.com or client@email.com"
          value={searchEmail}
          onChange={e=>{ setSearchEmail(e.target.value); setSearchError(""); }}
          type="email"
        />
        {searchError && (
          <div style={{ color:T.danger, fontSize:13, marginBottom:14, background:T.dangerFaint, padding:"10px 14px", borderRadius:8, border:`1px solid ${T.danger}33` }}>{searchError}</div>
        )}
        <div style={{ display:"flex", gap:10 }}>
          <Btn variant="ghost" onClick={()=>{ setNewMsgOpen(false); setSearchEmail(""); setSearchError(""); }} style={{ flex:1 }}>Cancel</Btn>
          <Btn variant="primary" onClick={startConversation} style={{ flex:2, padding:"11px" }}>{searching ? "Finding..." : "Start Conversation"}</Btn>
        </div>
      </Modal>
    </div>
  );
}

function ChatPage({ chat, setTab, user }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:"smooth" }); }, [msgs]);

  useEffect(() => {
    if (!chat || !user) return;
    loadMessages();

    // Realtime subscription
    const sub = supabase
      .channel("messages_" + chat.id + "_" + Date.now())
      .on("postgres_changes", {
        event:"INSERT", schema:"public", table:"messages",
        filter:`conversation_id=eq.${chat.id}`
      }, (payload) => {
        const newMsg = payload.new;
        // Only add if not already in list (avoid duplicates with optimistic update)
        setMsgs(prev => {
          const exists = prev.find(m => m.id === newMsg.id);
          if (exists) return prev;
          // If it was our temp message replaced, also skip
          const isMyMsg = newMsg.sender_id === user.id;
          if (isMyMsg) return prev; // already added optimistically
          return [...prev, newMsg];
        });
        // Mark as read if we're the recipient
        if (newMsg.recipient_id === user.id) {
          supabase.from("messages").update({ read:true }).eq("id", newMsg.id);
        }
      })
      .subscribe();

    // Fallback poll every 3 seconds in case realtime misses something
    const poll = setInterval(() => {
      if (!chat || !user) return;
      supabase.from("messages")
        .select("*")
        .eq("conversation_id", chat.id)
        .order("created_at", { ascending: true })
        .then(({ data }) => {
          if (data) setMsgs(data);
        });
    }, 1000);

    return () => {
      supabase.removeChannel(sub);
      clearInterval(poll);
    };
  }, [chat?.id, user?.id]);

  const loadMessages = async () => {
    if (!chat || !user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("messages")
      .select("*")
      .eq("conversation_id", chat.id)
      .order("created_at", { ascending: true });

    if (!error && data) {
      setMsgs(data);
      // Mark all as read
      await supabase.from("messages").update({ read:true }).eq("conversation_id", chat.id).eq("recipient_id", user.id);
    }
    setLoading(false);
  };

  const send = async () => {
    if (!input.trim() || !chat || !user) return;
    const text = input.trim();
    setInput("");
    setSending(true);

    const otherId = chat.participant_1 === user.id ? chat.participant_2 : chat.participant_1;

    // Optimistically add message to UI immediately
    const tempMsg = {
      id: "temp_" + Date.now(),
      conversation_id: chat.id,
      sender_id: user.id,
      recipient_id: otherId,
      content: text,
      created_at: new Date().toISOString(),
      read: false
    };
    setMsgs(prev => [...prev, tempMsg]);

    // Save to Supabase
    const { data: saved, error } = await supabase.from("messages").insert({
      conversation_id: chat.id,
      sender_id: user.id,
      recipient_id: otherId,
      content: text
    }).select().single();

    if (!error && saved) {
      // Replace temp message with real one
      setMsgs(prev => prev.map(m => m.id === tempMsg.id ? saved : m));
      // Send email notification
      try {
        const {data:rd} = await supabase.from("profiles").select("email, full_name").eq("id", otherId).single();
        if(rd?.email) sendEmailNotification("new_message", rd.email, { senderName: profile?.full_name||"Someone", messagePreview: text.substring(0,100) });
      } catch(e) {}
      // Update conversation last message
      await supabase.from("conversations")
        .update({ last_message: text, last_message_at: new Date().toISOString() })
        .eq("id", chat.id);
    } else if (error) {
      // Remove temp message on error
      setMsgs(prev => prev.filter(m => m.id !== tempMsg.id));
      alert("Failed to send message. Please try again.");
    }
    setSending(false);
  };

  if (!chat) return <div/>;

  const formatTime = (ts) => ts ? new Date(ts).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"}) : "";

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"calc(100vh - 120px)", maxWidth:680 }}>
      {/* Header */}
      <div style={{ display:"flex", gap:14, alignItems:"center", marginBottom:20, paddingBottom:16, borderBottom:`1px solid ${T.border}` }}>
        <button onClick={()=>setTab("messages")} style={{ background:"none", border:"none", color:T.blueLight, fontSize:14, fontWeight:600, cursor:"pointer" }}>← Back</button>
        <Ini initials={chat.initials||"??"} color={chat.color||T.blue} size={40}/>
        <div>
          <div style={{ fontWeight:600, color:T.text }}>{chat.other?.full_name||"Unknown"}</div>
          <div style={{ color:T.textMuted, fontSize:12 }}>BluTrades member</div>
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex:1, overflowY:"auto", display:"flex", flexDirection:"column", gap:10 }}>
        {loading ? (
          <div style={{ textAlign:"center", padding:"32px", color:T.textMuted, fontSize:14 }}>Loading messages…</div>
        ) : msgs.length === 0 ? (
          <div style={{ textAlign:"center", padding:"32px", color:T.textMuted, fontSize:14 }}>No messages yet. Send the first one!</div>
        ) : msgs.map((m, i) => {
          const isMe = m.sender_id === user?.id;
          return (
            <div key={i} style={{ display:"flex", justifyContent:isMe?"flex-end":"flex-start" }}>
              <div style={{ maxWidth:"68%", padding:"11px 15px", borderRadius:isMe?"12px 12px 3px 12px":"12px 12px 12px 3px", background:isMe?T.blue:T.card, color:T.text, fontSize:14, lineHeight:1.55, border:isMe?"none":`1px solid ${T.border}` }}>
                {m.content}
                <div style={{ color:isMe?"#ffffff55":T.textMuted, fontSize:11, marginTop:5, textAlign:"right" }}>{formatTime(m.created_at)}</div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef}/>
      </div>

      {/* Input */}
      <div style={{ display:"flex", gap:10, marginTop:16, paddingTop:16, borderTop:`1px solid ${T.border}` }}>
        <input
          value={input}
          onChange={e=>setInput(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&!sending&&send()}
          placeholder="Write a message..."
          style={{ flex:1, background:T.card, border:`1px solid ${T.border}`, borderRadius:8, padding:"11px 16px", color:T.text, fontSize:14, outline:"none" }}
        />
        <button onClick={send} disabled={sending||!input.trim()} style={{ background:T.blue, border:"none", borderRadius:8, width:44, height:44, color:"#fff", fontSize:18, cursor:"pointer", flexShrink:0, opacity:sending||!input.trim()?0.5:1 }}>↑</button>
      </div>
    </div>
  );
}

/* ── PROFILE WITH REAL UPLOAD ───────────────────────────────── */
function ProfilePage({ user, profile, role, isMobile, onSignOut, onShowLegal, onUpgradePro=()=>{} }) {
  const isClient = role === "client";
  const name = profile?.full_name || "Your Name";
  const initials = name.split(" ").map(w=>w[0]).join("").substring(0,2).toUpperCase();
  const [settingsPage,setSettingsPage]=useState(null);
  const [editing,setEditing]=useState(false);
  const [bio,setBio]=useState("");
  const [locationEdit,setLocationEdit]=useState(profile?.location||"");
  const [saving,setSaving]=useState(false);
  const [saved,setSaved]=useState(false);
  const [uploads,setUploads]=useState({wc:null,license:null,bg:null});

  const saveBio=async()=>{
    setSaving(true);
    if(role==="contractor") await supabase.from("contractor_profiles").update({bio}).eq("id",user.id);
    if(locationEdit.trim()) await supabase.from("profiles").update({location:locationEdit.trim()}).eq("id",user.id);
    setSaving(false); setSaved(true); setEditing(false);
    setTimeout(()=>setSaved(false),2000);
  };

  const handleUpload=async(file,type)=>{
    try {
      const ext=file.name.split(".").pop();
      const path=`${user.id}/${type}_${Date.now()}.${ext}`;
      const {error}=await supabase.storage.from("credentials").upload(path,file);
      if(error) throw error;
      const {data:{publicUrl}}=supabase.storage.from("credentials").getPublicUrl(path);
      await supabase.from("credentials").insert({contractor_id:user.id,type,file_url:publicUrl,file_name:file.name});
      setUploads(prev=>({...prev,[type]:{name:file.name,url:publicUrl}}));
    } catch(err) { console.error("Upload failed:",err); alert("Upload failed. Please try again."); }
  };

  return (
    <div>
      <Card style={{ marginBottom:20 }}>
        <div style={{ display:"flex", gap:20, alignItems:"flex-start", flexWrap:isMobile?"wrap":"nowrap" }}>
          <div style={{ position:"relative", flexShrink:0 }}>
            <Ini initials={initials} color={isClient?T.blue:T.blue} size={isMobile?64:80}/>
            <label style={{ position:"absolute", bottom:0, right:0, background:T.blue, borderRadius:"50%", width:24, height:24, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:12 }}>
              ✏
              <input type="file" accept="image/*" style={{ display:"none" }} onChange={async(e)=>{
                const file = e.target.files[0];
                if(!file) return;
                const ext = file.name.split(".").pop();
                const path = `${user.id}/avatar.${ext}`;
                const {error} = await supabase.storage.from("avatars").upload(path, file, {upsert:true});
                if(!error){
                  const {data:{publicUrl}} = supabase.storage.from("avatars").getPublicUrl(path);
                  await supabase.from("profiles").update({avatar_url:publicUrl}).eq("id",user.id);
                  alert("Profile photo updated!");
                }
              }}/>
            </label>
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:700, fontSize:isMobile?22:26, color:T.text, letterSpacing:-.5, marginBottom:4 }}>{name}</div>
            <div style={{ color:T.textMuted, fontSize:15, marginBottom:4, cursor:"pointer" }} onClick={async()=>{ const loc=prompt("Enter your city and state (e.g. Asheville, NC):", profile?.location||""); if(loc){ await supabase.from("profiles").update({location:loc}).eq("id",user.id); alert("Location updated! Refresh to see changes."); }}}>{profile?.location||"Location not set — click to set"}</div>
            {!isClient && <div style={{ color:T.blueLight, fontSize:14, fontWeight:600, marginBottom:4 }}>{profile?.trade||profile?.contractor_profiles?.trade||"Add your trade in settings"}</div>}
            <div style={{ color:T.textMuted, fontSize:14 }}>{user?.email}</div>
          </div>
        </div>
      </Card>

      <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:20, marginBottom:20 }}>
        <Card>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <div style={{ fontWeight:700, fontSize:15, color:T.text }}>About Me</div>
            {!editing
              ? <button onClick={()=>setEditing(true)} style={{ background:"none", border:"none", color:T.blueLight, fontWeight:600, cursor:"pointer", fontSize:13 }}>Edit</button>
              : <div style={{ display:"flex", gap:8 }}>
                  <button onClick={()=>setEditing(false)} style={{ background:"none", border:"none", color:T.textMuted, cursor:"pointer", fontSize:13 }}>Cancel</button>
                  <button onClick={saveBio} style={{ background:"none", border:"none", color:T.success, fontWeight:600, cursor:"pointer", fontSize:13 }}>{saving?"Saving...":"Save"}</button>
                </div>
            }
          </div>
          {editing
            ? <>
                <textarea value={bio} onChange={e=>setBio(e.target.value)} rows={4} placeholder="Tell clients about your experience and expertise..." style={{ width:"100%", background:T.surface, border:`1px solid ${T.border}`, borderRadius:8, padding:"10px 12px", color:T.text, fontSize:14, resize:"none", outline:"none", lineHeight:1.6, marginBottom:10 }}/>
                <Input label="City & State" placeholder="Asheville, NC" value={locationEdit} onChange={e=>setLocationEdit(e.target.value)}/>
              </>
            : <>
                <div style={{ color:bio?T.textSub:T.textMuted, fontSize:14, lineHeight:1.8, marginBottom:8 }}>{bio||"Add a bio to help clients understand your experience."}</div>
                <div style={{ color:T.textMuted, fontSize:13 }}>📍 {profile?.location||"Location not set"}</div>
              </>
          }
          {saved&&<div style={{ color:T.success, fontSize:13, marginTop:8 }}>✓ Saved</div>}
        </Card>

        <Card>
          <div style={{ fontWeight:700, fontSize:15, color:T.text, marginBottom:16 }}>{isClient?"Account":"Credentials"}</div>
          {!isClient&&<>
            <FileUploadBtn label="Workers' Comp Insurance" note="Upload certificate to get verified" uploaded={!!uploads.wc} fileName={uploads.wc?.name} onUpload={(f)=>handleUpload(f,"wc")}/>
            <FileUploadBtn label="State License / Certification" note="Upload to display verified badge" uploaded={!!uploads.license} fileName={uploads.license?.name} onUpload={(f)=>handleUpload(f,"license")}/>
            <FileUploadBtn label="Background Check" note="Authorize to unlock trust badge" uploaded={!!uploads.bg} fileName={uploads.bg?.name} onUpload={(f)=>handleUpload(f,"bg")}/>
          </>}
          <div style={{ display:"flex", flexDirection:"column", gap:0, marginTop:isClient?0:16 }}>
            {[["Email",user?.email],["Role",isClient?"Client":"Contractor"],["Member since",new Date(user?.created_at).toLocaleDateString("en-US",{month:"long",year:"numeric"})]].map(([l,v],i)=>(
              <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"10px 0", borderBottom:`1px solid ${T.border}` }}>
                <span style={{ color:T.textMuted, fontSize:14 }}>{l}</span>
                <span style={{ color:T.text, fontSize:14, fontWeight:600 }}>{v}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card>
        <div style={{ fontWeight:700, fontSize:15, color:T.text, marginBottom:16 }}>Settings</div>
        <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"repeat(2,1fr)", gap:8 }}>
          {!isClient&&<div onClick={()=>onUpgradePro()} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", background:"linear-gradient(135deg,#2D1B69,#1E3A5F)", borderRadius:8, padding:"13px 16px", cursor:"pointer", border:`1px solid #7C3AED44`, marginBottom:8 }}>
            <div><div style={{ fontWeight:700, color:"#fff", fontSize:14 }}>⭐ BluTrades Pro</div><div style={{ color:"#A78BFA", fontSize:12, marginTop:2 }}>Unlock Pro features — $29/month</div></div>
            <span style={{ color:"#A78BFA", fontWeight:700, fontSize:13 }}>Upgrade →</span>
          </div>}
          {[
            ["Personal Information","Name, email, phone","personal"],
            ["Payment Settings",isClient?"Cards & bank transfer":"Bank account & payouts","payment"],
            ["Notifications","Email and push preferences","notifications"],
            ["Security","Password and two-factor authentication","security"],
            ["Help & Support","FAQ and contact support","help"]
          ].map(([label,sub,key],i)=>(
            <div key={i} onClick={()=>setSettingsPage(key)} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", background:T.surface, borderRadius:8, padding:"13px 16px", cursor:"pointer", border:`1px solid ${T.border}` }}>
              <div><div style={{ fontWeight:600, color:T.text, fontSize:14 }}>{label}</div><div style={{ color:T.textMuted, fontSize:12, marginTop:2 }}>{sub}</div></div>
              <span style={{ color:T.textMuted }}>›</span>
            </div>
          ))}
          <div onClick={onSignOut} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", background:T.dangerFaint, borderRadius:8, padding:"13px 16px", cursor:"pointer", border:`1px solid ${T.danger}22` }}>
            <div style={{ fontWeight:600, color:T.danger, fontSize:14 }}>Sign Out</div>
            <span style={{ color:T.danger }}>›</span>
          </div>
        </div>
      </Card>

    {/* Settings Modals */}
    <PersonalInfoModal open={settingsPage==="personal"} onClose={()=>setSettingsPage(null)} profile={profile} user={user}/>

    <Modal open={settingsPage==="payment"} onClose={()=>setSettingsPage(null)} title={isClient?"Payment Settings":"Payout Settings"}>
      <div style={{background:T.card,borderRadius:8,padding:"24px",marginBottom:20,border:`1px solid ${T.border}`,textAlign:"center"}}>
        <div style={{fontSize:36,marginBottom:12}}>💳</div>
        <div style={{fontWeight:600,color:T.text,marginBottom:6}}>{isClient?"Payment Methods":"Bank Account"}</div>
        <div style={{color:T.textMuted,fontSize:13,marginBottom:16,lineHeight:1.6}}>Stripe-powered secure payments coming soon. You'll be able to {isClient?"add cards and bank accounts":"connect your bank for fast payouts"}.</div>
        <Badge color={T.amber}>Coming Soon</Badge>
      </div>
      <Btn variant="ghost" full onClick={()=>setSettingsPage(null)}>Close</Btn>
    </Modal>

    <NotifModal open={settingsPage==="notifications"} onClose={()=>setSettingsPage(null)}/>

    <Modal open={settingsPage==="security"} onClose={()=>setSettingsPage(null)} title="Security Settings">
      <Input label="Current Password" placeholder="Enter current password" value="" onChange={()=>{}} type="password"/>
      <Input label="New Password" placeholder="At least 6 characters" value="" onChange={()=>{}} type="password"/>
      <Input label="Confirm New Password" placeholder="Repeat new password" value="" onChange={()=>{}} type="password"/>
      <Btn variant="primary" full onClick={()=>setSettingsPage(null)} style={{padding:"12px",marginBottom:12}}>Update Password</Btn>
      <Divider/>
      <div style={{fontWeight:700,color:T.text,marginBottom:8,marginTop:4}}>Two-Factor Authentication</div>
      <div style={{color:T.textMuted,fontSize:13,marginBottom:12}}>Add an extra layer of security to your account.</div>
      <Btn variant="subtle" full>Enable 2FA — Coming Soon</Btn>
    </Modal>

    <Modal open={settingsPage==="help"} onClose={()=>setSettingsPage(null)} title="Help & Support">
      <a href="mailto:support@blutrades.app" style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:T.surface,borderRadius:8,padding:"13px 16px",marginBottom:8,border:`1px solid ${T.border}`,cursor:"pointer",textDecoration:"none"}}>
        <div><div style={{fontWeight:600,color:T.text,fontSize:14}}>📧 Email Support</div><div style={{color:T.textMuted,fontSize:12,marginTop:2}}>support@blutrades.app</div></div>
        <span style={{color:T.textMuted}}>›</span>
      </a>
      <div onClick={()=>{setSettingsPage(null);onShowLegal&&onShowLegal("terms");}} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:T.surface,borderRadius:8,padding:"13px 16px",marginBottom:8,border:`1px solid ${T.border}`,cursor:"pointer"}}>
        <div><div style={{fontWeight:600,color:T.text,fontSize:14}}>📋 Terms of Service</div><div style={{color:T.textMuted,fontSize:12,marginTop:2}}>View our terms</div></div>
        <span style={{color:T.textMuted}}>›</span>
      </div>
      <div onClick={()=>{setSettingsPage(null);onShowLegal&&onShowLegal("privacy");}} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:T.surface,borderRadius:8,padding:"13px 16px",marginBottom:8,border:`1px solid ${T.border}`,cursor:"pointer"}}>
        <div><div style={{fontWeight:600,color:T.text,fontSize:14}}>🔒 Privacy Policy</div><div style={{color:T.textMuted,fontSize:12,marginTop:2}}>View our privacy policy</div></div>
        <span style={{color:T.textMuted}}>›</span>
      </div>
      <Btn variant="ghost" full onClick={()=>setSettingsPage(null)} style={{marginTop:4}}>Close</Btn>
    </Modal>
    </div>
  );
}

/* ── REVIEW MODAL ───────────────────────────────────────────── */
function ReviewModal({ open, onClose, job, contractorId, clientId }) {
  const [stars, setStars] = useState(5);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(()=>{
    if(open) { setStars(5); setText(""); setDone(false); }
  },[open]);

  const submit = async() => {
    if(!text.trim()) return;
    setSaving(true);
    await supabase.from("reviews").insert({
      job_id: job?.id,
      contractor_id: contractorId,
      reviewer_id: clientId,
      rating: stars,
      content: text.trim(),
    });
    // Update job status to completed
    await supabase.from("jobs").update({ status:"completed" }).eq("id", job?.id);
    setSaving(false);
    setDone(true);
    setTimeout(()=>onClose(), 1500);
  };

  if(!open || !job) return null;
  return (
    <div style={{ position:"fixed", inset:0, background:"#00000099", display:"flex", alignItems:"center", justifyContent:"center", zIndex:400, backdropFilter:"blur(4px)", padding:20 }}>
      <div style={{ background:T.surface, borderRadius:12, padding:"28px", width:"100%", maxWidth:480, border:`1px solid ${T.border}`, boxShadow:"0 20px 60px #00000066" }}>
        {!done ? <>
          <div style={{ fontWeight:700, fontSize:18, color:T.text, marginBottom:4 }}>Leave a Review</div>
          <div style={{ color:T.textMuted, fontSize:14, marginBottom:20 }}>{job?.title}</div>

          {/* Star rating */}
          <div style={{ marginBottom:20 }}>
            <div style={{ fontSize:12, color:"#C8DCF0", marginBottom:10, fontWeight:700, letterSpacing:.6, textTransform:"uppercase" }}>Rating</div>
            <div style={{ display:"flex", gap:8 }}>
              {[1,2,3,4,5].map(s=>(
                <button
                  key={s}
                  onClick={()=>setStars(s)}
                  onMouseEnter={()=>setHover(s)}
                  onMouseLeave={()=>setHover(0)}
                  style={{ background:(hover||stars)>=s?T.amberFaint:T.card, border:`1px solid ${(hover||stars)>=s?T.amber+"44":T.border}`, borderRadius:8, width:52, height:52, fontSize:26, color:(hover||stars)>=s?T.amber:T.textMuted, cursor:"pointer", transition:"all .15s" }}
                >★</button>
              ))}
            </div>
            <div style={{ color:T.textMuted, fontSize:13, marginTop:8 }}>
              {["","Poor","Below average","Good","Great","Excellent!"][hover||stars]}
            </div>
          </div>

          {/* Review text */}
          <div style={{ marginBottom:20 }}>
            <div style={{ fontSize:12, color:"#C8DCF0", marginBottom:6, fontWeight:700, letterSpacing:.6, textTransform:"uppercase" }}>Your Review</div>
            <textarea
              value={text}
              onChange={e=>setText(e.target.value)}
              placeholder="How was the quality of work? Was the contractor professional and on time?"
              rows={4}
              style={{ width:"100%", background:T.card, border:`1px solid ${T.border}`, borderRadius:8, padding:"12px 14px", color:T.text, fontSize:14, resize:"none", outline:"none", lineHeight:1.6 }}
            />
          </div>

          <div style={{ display:"flex", gap:10 }}>
            <button onClick={onClose} style={{ flex:1, background:T.card, border:`1px solid ${T.border}`, borderRadius:8, padding:"11px", color:T.textSub, fontSize:14, fontWeight:600, cursor:"pointer" }}>Cancel</button>
            <button
              onClick={submit}
              disabled={!text.trim()||saving}
              style={{ flex:2, background:text.trim()&&!saving?T.blue:T.card, border:"none", borderRadius:8, padding:"11px", color:text.trim()&&!saving?"#fff":T.textMuted, fontSize:14, fontWeight:700, cursor:text.trim()&&!saving?"pointer":"not-allowed" }}
            >{saving?"Submitting…":"Submit Review"}</button>
          </div>
        </> : (
          <div style={{ textAlign:"center", padding:"16px 0" }}>
            <div style={{ fontSize:48, marginBottom:12 }}>⭐</div>
            <div style={{ fontWeight:700, fontSize:20, color:T.text, marginBottom:8 }}>Review Submitted!</div>
            <div style={{ color:T.textMuted, fontSize:14 }}>Thank you for your feedback.</div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── CONTRACT MODAL ─────────────────────────────────────────── */
function ContractModal({ open, onClose }) {
  const [step,setStep]=useState(1);
  const [f,setF]=useState({contractor:"",trade:"Electrician",client:"",address:"",description:"",amount:"",startDate:"",duration:"",contractType:"Fixed Price",requirePhotos:false});
  const [generated,setGenerated]=useState("");
  const [loading,setLoading]=useState(false);
  const [signed,setSigned]=useState(false);
  const set=(k,v)=>setF(p=>({...p,[k]:v}));
  const reset=()=>{setStep(1);setF({contractor:"",trade:"Electrician",client:"",address:"",description:"",amount:"",startDate:"",duration:"",contractType:"Fixed Price"});setGenerated("");setSigned(false);onClose();};
  const generate=async()=>{setStep(2);setLoading(true);try{const t=await genContract(f);setGenerated(t);}catch{setGenerated("Contract generation failed.");}setLoading(false);};
  return (
    <Modal open={open} onClose={reset} title="Generate Contract">
      {step===1&&<>
        <p style={{ color:T.textSub, fontSize:14, marginBottom:20 }}>Fill in the details and AI generates a legally sound contract ready for e-signature.</p>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <Input label="Client Name" placeholder="Full legal name" value={f.client} onChange={e=>set("client",e.target.value)}/>
          <Input label="Start Date" placeholder="05/01/2026" value={f.startDate} onChange={e=>set("startDate",e.target.value)}/>
        </div>
        <Input label="Job Address" placeholder="123 Main St, Houston TX" value={f.address} onChange={e=>set("address",e.target.value)}/>
        <Input label="Scope of Work" placeholder="Describe the full scope..." value={f.description} onChange={e=>set("description",e.target.value)} multiline rows={3}/>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <Input label="Total Amount ($)" placeholder="1,550" value={f.amount} onChange={e=>set("amount",e.target.value)}/>
          <Input label="Duration" placeholder="e.g. 1 day, 2 weeks" value={f.duration} onChange={e=>set("duration",e.target.value)}/>
        </div>
        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:12, color:"#C8DCF0", marginBottom:8, fontWeight:700, letterSpacing:.6, textTransform:"uppercase" }}>Contract Type</div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            {["Fixed Price","Time & Materials","Hourly Rate","Cost Plus"].map(t=><button key={t} onClick={()=>set("contractType",t)} style={{ background:f.contractType===t?T.blue:T.surface, color:f.contractType===t?"#fff":T.textSub, border:`1px solid ${f.contractType===t?T.blue:T.border}`, borderRadius:6, padding:"7px 14px", fontSize:13, fontWeight:600, cursor:"pointer" }}>{t}</button>)}
          </div>
        </div>
        <Btn variant="primary" full onClick={generate} style={{ padding:"12px", fontSize:15 }}>Generate Contract</Btn>
      </>}
      {step===2&&<>
        {loading
          ? <div style={{ textAlign:"center", padding:"40px 20px" }}><div style={{ color:T.textMuted, fontSize:14, marginBottom:8 }}>Generating your contract…</div><div style={{ color:T.textSub, fontSize:13 }}>This takes about 10 seconds.</div></div>
          : <>
            <div style={{ background:T.card, borderRadius:8, padding:16, marginBottom:20, maxHeight:300, overflowY:"auto", border:`1px solid ${T.border}` }}>
              <div style={{ color:T.textSub, fontSize:13, lineHeight:2, whiteSpace:"pre-wrap" }}>{generated}</div>
            </div>
            {!signed
              ? <div style={{ display:"flex", gap:10 }}><Btn variant="ghost" onClick={()=>setStep(1)} style={{ flex:1 }}>Edit Details</Btn><Btn variant="primary" onClick={()=>setSigned(true)} style={{ flex:2, padding:"12px" }}>Sign & Send to Client</Btn></div>
              : <div style={{ textAlign:"center", padding:"16px 0" }}>
                  <div style={{ width:48, height:48, borderRadius:"50%", background:T.successFaint, border:`1px solid ${T.success}33`, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 14px", fontSize:20, color:T.success }}>✓</div>
                  <div style={{ fontWeight:700, fontSize:18, color:T.text, marginBottom:6 }}>Contract Sent</div>
                  <div style={{ color:T.textMuted, fontSize:14, marginBottom:20 }}>Both parties receive a signed PDF by email.</div>
                  <Btn variant="primary" onClick={reset} style={{ padding:"10px 28px" }}>Done</Btn>
                </div>
            }
          </>
        }
      </>}
    </Modal>
  );
}

function VouchModal({ open, onClose, contractor }) {
  const [rating,setRating]=useState(5);
  const [text,setText]=useState("");
  const [submitted,setSubmitted]=useState(false);
  const reset=()=>{setRating(5);setText("");setSubmitted(false);onClose();};
  return (
    <Modal open={open} onClose={reset} title="Leave a Vouch">
      {!submitted?<>
        <p style={{ color:T.textSub, fontSize:14, marginBottom:20 }}>Your vouch is a verified endorsement visible on this contractor's profile.</p>
        {contractor&&<div style={{ background:T.card, borderRadius:8, padding:"12px 14px", marginBottom:20, display:"flex", gap:12, alignItems:"center", border:`1px solid ${T.border}` }}><Ini initials={contractor.initials} color={contractor.color} size={42}/><div><div style={{ fontWeight:600, color:T.text }}>{contractor.name}</div><div style={{ color:T.textMuted, fontSize:13 }}>{contractor.trade}</div></div></div>}
        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:12, color:"#C8DCF0", marginBottom:10, fontWeight:700, letterSpacing:.6, textTransform:"uppercase" }}>Rating</div>
          <div style={{ display:"flex", gap:8 }}>{[1,2,3,4,5].map(s=><button key={s} onClick={()=>setRating(s)} style={{ background:s<=rating?T.amberFaint:T.card, border:`1px solid ${s<=rating?T.amber+"44":T.border}`, borderRadius:8, width:46, height:46, fontSize:22, color:s<=rating?T.amber:T.textMuted, cursor:"pointer" }}>★</button>)}</div>
        </div>
        <Input label="Your Vouch" placeholder="Describe your experience working with this contractor." value={text} onChange={e=>setText(e.target.value)} multiline rows={4}/>
        <Btn variant="primary" full onClick={()=>text.trim()&&setSubmitted(true)} style={{ marginTop:8, padding:"12px" }}>Submit Vouch</Btn>
      </>:<div style={{ textAlign:"center", padding:"24px 0" }}>
        <div style={{ width:48, height:48, borderRadius:"50%", background:T.successFaint, border:`1px solid ${T.success}33`, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 14px", fontSize:20, color:T.success }}>✓</div>
        <div style={{ fontWeight:700, fontSize:18, color:T.text, marginBottom:6 }}>Vouch Submitted</div>
        <div style={{ color:T.textMuted, fontSize:14, marginBottom:20 }}>Your vouch is now live on this contractor's profile.</div>
        <Btn variant="primary" onClick={reset} style={{ padding:"10px 28px" }}>Done</Btn>
      </div>}
    </Modal>
  );
}

function BidderProfileModal({ open, onClose, contractor }) {
  const [photoIdx,setPhotoIdx]=useState(0);
  if(!contractor) return null;
  const photoColors=["#2563EB","#0D9488","#7C3AED","#B45309","#854D0E","#0369A1"];
  return (
    <div style={{ position:"fixed", inset:0, background:"#00000099", display:"flex", alignItems:"center", justifyContent:"center", zIndex:400, backdropFilter:"blur(4px)", padding:20 }}>
      <div style={{ background:T.surface, borderRadius:12, width:"100%", maxWidth:640, border:`1px solid ${T.border}`, maxHeight:"90vh", overflowY:"auto", boxShadow:"0 20px 60px #00000066" }}>
        <div style={{ background:`${contractor.color}18`, borderBottom:`1px solid ${T.border}`, padding:"24px 28px", display:"flex", gap:18, alignItems:"flex-start" }}>
          <div style={{ position:"relative", flexShrink:0 }}><Ini initials={contractor.initials} color={contractor.color} size={72}/><OnlineDot online={contractor.online}/></div>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:700, fontSize:22, color:T.text, letterSpacing:-.5, marginBottom:4 }}>{contractor.name}</div>
            <div style={{ color:T.textMuted, fontSize:14, marginBottom:8 }}>{contractor.trade} · {contractor.loc}</div>
            <Stars n={contractor.rating} size={14}/>
            <div style={{ fontWeight:700, fontSize:18, color:T.text, marginTop:6 }}>{contractor.rate}</div>
          </div>
          <button onClick={onClose} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:6, width:34, height:34, color:T.textSub, fontSize:16, cursor:"pointer", flexShrink:0 }}>✕</button>
        </div>
        <div style={{ padding:"24px 28px" }}>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:20 }}>
            {contractor.verified&&<Badge color={T.success}>Identity Verified</Badge>}
            {contractor.insured&&<Badge color={T.success}>Insured · Exp {contractor.expiry}</Badge>}
            {contractor.licensed?<Badge color={T.blueLight}>Licensed</Badge>:<Badge color={T.amber}>Unlicensed</Badge>}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:24 }}>
            {[[contractor.jobs,"Jobs Completed"],[contractor.reviews,"Reviews"],[contractor.rate,"Hourly Rate"]].map(([v,l],i)=>(
              <div key={i} style={{ background:T.card, borderRadius:8, padding:"14px 16px", border:`1px solid ${T.border}`, textAlign:"center" }}>
                <div style={{ fontWeight:700, fontSize:20, color:T.text }}>{v}</div>
                <div style={{ color:T.textMuted, fontSize:12, marginTop:3 }}>{l}</div>
              </div>
            ))}
          </div>
          <div style={{ marginBottom:20 }}>
            <div style={{ fontWeight:700, fontSize:15, color:T.text, marginBottom:14 }}>Work Photos</div>
            {(!contractor.photos||contractor.photos===0)?<div style={{ background:T.card, borderRadius:10, padding:"32px", textAlign:"center", color:T.textMuted, fontSize:14, marginBottom:10 }}>No photos uploaded yet</div>:
            <div style={{ background:`linear-gradient(135deg,${photoColors[photoIdx%photoColors.length]}33,${T.card})`, borderRadius:10, height:200, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:10, position:"relative", border:`1px solid ${T.border}` }}>
              <div style={{ textAlign:"center" }}><div style={{ fontSize:40, marginBottom:8, color:photoColors[photoIdx%photoColors.length], opacity:.5 }}>◼</div><div style={{ color:T.textMuted, fontSize:13, fontWeight:600 }}>Photo {photoIdx+1} of {contractor.photos}</div><div style={{ color:T.textMuted, fontSize:12, marginTop:4 }}>{photoIdx%2===0?"Before":"After"}</div></div>
              <div style={{ position:"absolute", bottom:12, right:12, display:"flex", gap:6 }}>
                <button onClick={()=>setPhotoIdx(i=>Math.max(0,i-1))} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:6, width:30, height:30, color:T.text, cursor:"pointer", fontSize:15 }}>‹</button>
                <button onClick={()=>setPhotoIdx(i=>Math.min(contractor.photos-1,i+1))} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:6, width:30, height:30, color:T.text, cursor:"pointer", fontSize:15 }}>›</button>
              </div>
            </div>}
            <div style={{ display:"flex", gap:8 }}>
              {Array.from({length:Math.min(contractor.photos,6)}).map((_,i)=>(
                <div key={i} onClick={()=>setPhotoIdx(i)} style={{ flex:1, height:48, borderRadius:7, background:`${photoColors[i%photoColors.length]}22`, border:`2px solid ${i===photoIdx?contractor.color:T.border}`, cursor:"pointer" }}/>
              ))}
            </div>
          </div>
          <div style={{ marginBottom:20 }}>
            <div style={{ fontWeight:700, fontSize:15, color:T.text, marginBottom:10 }}>About</div>
            <div style={{ color:T.textSub, fontSize:14, lineHeight:1.8, marginBottom:14 }}>{contractor.bio}</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>{contractor.skills.map((s,i)=><span key={i} style={{ background:T.card, color:T.textSub, border:`1px solid ${T.border}`, borderRadius:6, padding:"4px 12px", fontSize:13 }}>{s}</span>)}</div>
          </div>
          <div style={{ marginBottom:20 }}>
            <div style={{ fontWeight:700, fontSize:15, color:T.text, marginBottom:14 }}>Reviews & Vouches <span style={{ color:T.textMuted, fontWeight:400, fontSize:13 }}>({contractor.reviews} total)</span></div>
            {contractor.reviewList.map((r,i)=>(
              <div key={i} style={{ background:T.card, borderRadius:8, padding:"14px 16px", marginBottom:10, border:`1px solid ${T.border}` }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                  <div><span style={{ fontWeight:600, color:T.text }}>{r.name}</span><span style={{ color:T.textMuted, fontSize:12, marginLeft:8 }}>{r.role}</span></div>
                  <div style={{ textAlign:"right" }}><Stars n={r.stars} size={12}/><div style={{ color:T.textMuted, fontSize:11, marginTop:2 }}>{r.date}</div></div>
                </div>
                <div style={{ color:T.textSub, fontSize:14, lineHeight:1.6 }}>"{r.text}"</div>
              </div>
            ))}
          </div>
          <div style={{ display:"flex", gap:10 }}>
            <Btn variant="ghost" onClick={onClose} style={{ flex:1 }}>Close</Btn>
            <Btn variant="primary" onClick={onClose} style={{ flex:2, padding:"12px" }}>Back to Bids</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── NAVIGATION ─────────────────────────────────────────────── */
function Sidebar({ tab, setTab, role, profile, onSignOut, unread=0, onShowLegal=()=>{} }) {
  const isClient=tab.startsWith("cl_");
  const cNav=[{id:"c_home",label:"Dashboard",icon:"▣"},{id:"c_jobs",label:"Find Work",icon:"⊕"},{id:"c_bids",label:"My Bids",icon:"◈"},{id:"c_tracker",label:"Job Tracker",icon:"◫"},{id:"contracts",label:"Contracts",icon:"◻"},{id:"messages",label:"Messages",icon:"◇"},{id:"c_profile",label:"Profile",icon:"◉"}];
  const clNav=[{id:"cl_home",label:"Dashboard",icon:"▣"},{id:"cl_post",label:"Post a Job",icon:"⊕"},{id:"cl_projects",label:"My Projects",icon:"◫"},{id:"cl_browse",label:"Browse Pros",icon:"◈"},{id:"messages",label:"Messages",icon:"◇"},{id:"cl_profile",label:"Profile",icon:"◉"}];
  const nav=isClient?clNav:cNav;
  const mainTab=tab==="c_jobDetail"?"c_jobs":tab==="cl_proDetail"?"cl_browse":tab==="cl_reviewBids"?"cl_home":tab==="chat"?"messages":nav.find(n=>n.id===tab)?tab:(isClient?"cl_home":"c_home");
  const name=profile?.full_name||"User";
  const initials=name.split(" ").map(w=>w[0]).join("").substring(0,2).toUpperCase();
  return (
    <div style={{ width:216, flexShrink:0, background:T.surface, borderRight:`1px solid ${T.border}`, display:"flex", flexDirection:"column", height:"100vh", position:"sticky", top:0 }}>
      <div style={{ padding:"24px 20px 20px" }}>
        <div style={{ fontWeight:800, fontSize:20, color:T.text, letterSpacing:-.5, marginBottom:16 }}>Blu<span style={{ color:T.blue }}>Trades</span></div>
        <div style={{ display:"flex", gap:6, marginTop:4 }}>
          <button onClick={()=>setTab("c_home")} style={{ flex:1, background:tab.startsWith("c_")&&!tab.startsWith("cl_")?"#2563EB":"transparent", color:tab.startsWith("c_")&&!tab.startsWith("cl_")?"#fff":"#6B8BA4", border:`1px solid ${tab.startsWith("c_")&&!tab.startsWith("cl_")?"#2563EB":"#2A3F58"}`, borderRadius:7, padding:"7px 8px", fontSize:11, fontWeight:700, cursor:"pointer", transition:"all .2s" }}>Contractor</button>
          <button onClick={()=>setTab("cl_home")} style={{ flex:1, background:tab.startsWith("cl_")?"#2563EB":"transparent", color:tab.startsWith("cl_")?"#fff":"#6B8BA4", border:`1px solid ${tab.startsWith("cl_")?"#2563EB":"#2A3F58"}`, borderRadius:7, padding:"7px 8px", fontSize:11, fontWeight:700, cursor:"pointer", transition:"all .2s" }}>Client</button>
        </div>
      </div>
      <div style={{ flex:1, padding:"0 10px", overflowY:"auto" }}>
        {nav.map(item=>{
          const active=mainTab===item.id;
          return <button key={item.id} onClick={()=>setTab(item.id)} style={{ width:"100%", display:"flex", alignItems:"center", gap:10, padding:"9px 12px", background:active?T.card:"transparent", border:"none", borderRadius:8, cursor:"pointer", color:active?T.text:"#FFFFFF", marginBottom:2, fontWeight:active?700:500, fontSize:14, borderLeft:`2px solid ${active?(isClient?T.teal:T.blue):"transparent"}` }}>
            <span style={{ fontSize:14, opacity:.7 }}>{item.icon}</span>
            <span>{item.label}</span>
            {item.id==="messages"&&unread>0&&<div style={{ marginLeft:"auto", background:T.blue, borderRadius:10, minWidth:18, height:18, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:11, fontWeight:700, padding:"0 5px" }}>{unread}</div>}
          </button>;
        })}
      </div>
      <div style={{ padding:"14px 16px", borderTop:`1px solid ${T.border}` }}>
        <div style={{ display:"flex", gap:10, alignItems:"center", marginBottom:10 }}>
          <Ini initials={initials} color={T.blue} size={34}/>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontWeight:600, fontSize:13, color:T.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{name}</div>
            <div style={{ color:"#8BA5C0", fontSize:12 }}>{isClient?"Client":"Contractor"}</div>
          </div>
        </div>
        <div style={{ display:"flex", gap:12 }}>
          <button onClick={()=>onShowLegal("terms")} style={{ background:"none", border:"none", color:T.textMuted, fontSize:11, cursor:"pointer", padding:0 }}>Terms</button>
          <button onClick={()=>onShowLegal("privacy")} style={{ background:"none", border:"none", color:T.textMuted, fontSize:11, cursor:"pointer", padding:0 }}>Privacy</button>
        </div>
      </div>
    </div>
  );
}

function BottomNav({ tab, setTab, role, unread=0 }) {
  const isClient=tab.startsWith("cl_");
  const cNav=[{id:"c_home",label:"Home",icon:"▣"},{id:"c_jobs",label:"Find Work",icon:"⊕"},{id:"c_bids",label:"My Bids",icon:"◈"},{id:"messages",label:"Messages",icon:"◇"},{id:"c_profile",label:"Profile",icon:"◉"}];
  const clNav=[{id:"cl_home",label:"Home",icon:"▣"},{id:"cl_post",label:"Post Job",icon:"⊕"},{id:"cl_browse",label:"Browse Pros",icon:"◈"},{id:"messages",label:"Messages",icon:"◇"},{id:"cl_profile",label:"Profile",icon:"◉"}];
  const nav=isClient?clNav:cNav;
  const mainTab=tab==="c_jobDetail"?"c_jobs":tab==="cl_proDetail"?"cl_browse":tab==="chat"?"messages":nav.find(n=>n.id===tab)?tab:(isClient?"cl_home":"c_home");
  const accent=isClient?T.teal:T.blue;
  return (
    <div style={{ position:"fixed", bottom:0, left:0, right:0, background:`${T.surface}F0`, borderTop:`1px solid ${T.border}`, display:"flex", zIndex:100, backdropFilter:"blur(12px)" }}>
      {nav.map(item=>{const active=mainTab===item.id;return <button key={item.id} onClick={()=>setTab(item.id)} style={{ flex:1, padding:"10px 0 12px", background:"none", border:"none", cursor:"pointer", color:active?accent:"#FFFFFF", display:"flex", flexDirection:"column", alignItems:"center", gap:3, borderTop:`2px solid ${active?accent:"transparent"}` }}><span style={{ fontSize:16 }}>{item.icon}</span><span style={{ fontSize:10, fontWeight:active?700:400 }}>{item.label}</span></button>;})}
    </div>
  );
}

/* ── LEGAL PAGES ────────────────────────────────────────────── */
function LegalPage({ type, setTab, isClient }) {
  const backTab = isClient ? "cl_home" : "c_home";
  const isTerms = type === "terms";
  return (
    <div style={{ maxWidth:720, margin:"0 auto" }}>
      <button onClick={()=>setTab(backTab)} style={{ background:"none", border:"none", color:T.blueLight, fontSize:14, fontWeight:600, cursor:"pointer", marginBottom:24 }}>← Back</button>
      <div style={{ fontWeight:800, fontSize:28, color:T.text, letterSpacing:-.5, marginBottom:8 }}>{isTerms?"Terms of Service":"Privacy Policy"}</div>
      <div style={{ color:T.textMuted, fontSize:14, marginBottom:32 }}>Last updated: April 24, 2026</div>

      {isTerms ? <>
        {[
          ["1. Acceptance of Terms","By accessing or using BluTrades (blutrades.app), you agree to be bound by these Terms of Service. If you do not agree, do not use our platform. These terms apply to all users including contractors, homeowners, and general contractors."],
          ["2. Description of Service","BluTrades is a marketplace platform that connects contractors and tradespeople with homeowners and general contractors. We facilitate job posting, bidding, contract generation, messaging, and payment processing. BluTrades is not a party to any agreement between contractors and clients."],
          ["3. User Accounts","You must provide accurate information when creating an account. You are responsible for maintaining the confidentiality of your account credentials. You must be at least 18 years old to use BluTrades. One person may not maintain more than one account."],
          ["4. Contractor Responsibilities","Contractors represent that all credentials, licenses, and insurance information provided is accurate and current. Contractors are solely responsible for the quality of their work and compliance with all applicable laws, building codes, and regulations. BluTrades does not guarantee the quality of contractor services."],
          ["5. Client Responsibilities","Clients are responsible for providing accurate job descriptions and having the legal right to authorize the work requested. Clients must make payment through BluTrades for jobs facilitated through the platform."],
          ["6. Platform Fees","BluTrades charges a service fee on transactions processed through the platform. The current fee structure is displayed at checkout. Fees are non-refundable except as required by law."],
          ["7. Payments","All payments must be processed through BluTrades. Direct payments between contractors and clients outside the platform are a violation of these terms. BluTrades uses Stripe for payment processing and does not store card information."],
          ["8. Prohibited Conduct","Users may not post false or misleading information, circumvent platform fees, harass other users, use the platform for illegal activities, or attempt to reverse engineer the platform."],
          ["9. Limitation of Liability","BluTrades is not liable for the quality of contractor work, disputes between users, or any damages arising from use of the platform beyond the fees paid to BluTrades in the preceding 12 months."],
          ["10. Changes to Terms","We may update these terms at any time. Continued use of BluTrades after changes constitutes acceptance of the new terms. We will notify users of material changes by email."],
          ["11. Contact","For questions about these terms, contact us at support@blutrades.app"],
        ].map(([title,body],i)=>(
          <Card key={i} style={{ marginBottom:12 }}>
            <div style={{ fontWeight:700, fontSize:15, color:T.text, marginBottom:8 }}>{title}</div>
            <div style={{ color:T.textSub, fontSize:14, lineHeight:1.8 }}>{body}</div>
          </Card>
        ))}
      </> : <>
        {[
          ["Information We Collect","We collect information you provide when creating an account (name, email, location, trade), information you provide when posting or bidding on jobs, messages sent through the platform, credential documents you upload, payment information processed through Stripe, and usage data including device type and pages visited."],
          ["How We Use Your Information","We use your information to operate and improve the BluTrades platform, match contractors with relevant job opportunities, facilitate communication between users, process payments, verify credentials, send notifications about your account and jobs, and comply with legal obligations."],
          ["Information Sharing","We do not sell your personal information. We share your information with other users as necessary to facilitate the marketplace (e.g., showing your profile to clients), with Stripe for payment processing, with credential verification services, and with law enforcement when required by law."],
          ["Data Security","We use industry-standard encryption and security practices. All data is stored on Supabase infrastructure with row-level security. Payment data is handled exclusively by Stripe and never stored on BluTrades servers."],
          ["Your Rights","You may access, update, or delete your account information at any time through your profile settings. You may request a copy of your data by contacting support@blutrades.app. You may opt out of marketing communications at any time."],
          ["Cookies","We use essential cookies to keep you logged in and remember your preferences. We do not use advertising cookies or sell browsing data."],
          ["Data Retention","We retain your account data for as long as your account is active. After account deletion, we retain transaction records for 7 years as required by law."],
          ["Children's Privacy","BluTrades is not intended for users under 18. We do not knowingly collect information from minors."],
          ["Changes to This Policy","We may update this policy periodically. We will notify you of significant changes by email. Continued use of BluTrades after changes constitutes acceptance."],
          ["Contact Us","For privacy questions or requests, contact us at support@blutrades.app or write to BluTrades, Hendersonville, NC 28792."],
        ].map(([title,body],i)=>(
          <Card key={i} style={{ marginBottom:12 }}>
            <div style={{ fontWeight:700, fontSize:15, color:T.text, marginBottom:8 }}>{title}</div>
            <div style={{ color:T.textSub, fontSize:14, lineHeight:1.8 }}>{body}</div>
          </Card>
        ))}
      </>}
      <div style={{ textAlign:"center", marginTop:24, color:T.textMuted, fontSize:13 }}>Questions? Email us at <a href="mailto:support@blutrades.app" style={{ color:T.blueLight }}>support@blutrades.app</a></div>
    </div>
  );
}


/* ── EMAIL NOTIFICATIONS ─────────────────────────────────────── */
const sendEmailNotification = async(type, recipientEmail, data) => {
  try {
    let subject, html;
    if(type === "new_bid") {
      subject = `New bid on your job: ${data.jobTitle}`;
      html = `
        <div style="font-family: DM Sans, sans-serif; max-width: 600px; margin: 0 auto; background: #0B1929; color: #E2EAF4; padding: 32px; border-radius: 12px;">
          <div style="margin-bottom: 24px;">
            <span style="color: #1D6CE8; font-size: 24px; font-weight: 800;">Blu</span><span style="color: #E2EAF4; font-size: 24px; font-weight: 800;">Trades</span>
          </div>
          <h2 style="color: #E2EAF4; margin-bottom: 8px;">You have a new bid!</h2>
          <p style="color: #8BA7BF; margin-bottom: 20px;">${data.contractorName} has placed a bid on your job.</p>
          <div style="background: #112236; border-radius: 8px; padding: 20px; margin-bottom: 20px; border: 1px solid #1E3148;">
            <div style="color: #8BA7BF; font-size: 13px; margin-bottom: 4px;">JOB</div>
            <div style="color: #E2EAF4; font-weight: 700; font-size: 16px; margin-bottom: 12px;">${data.jobTitle}</div>
            <div style="color: #8BA7BF; font-size: 13px; margin-bottom: 4px;">BID AMOUNT</div>
            <div style="color: #F59E0B; font-weight: 800; font-size: 24px;">$${data.bidAmount}</div>
          </div>
          <a href="https://blutrades.app" style="background: #1D6CE8; color: #fff; padding: 12px 24px; border-radius: 8px; text-decoration: none; font-weight: 700; display: inline-block; margin-bottom: 20px;">Review Bid</a>
          <p style="color: #4F6880; font-size: 12px;">You're receiving this because you have a job posted on BluTrades.</p>
        </div>
      `;
    } else if(type === "new_message") {
      subject = `New message from ${data.senderName} on BluTrades`;
      html = `
        <div style="font-family: DM Sans, sans-serif; max-width: 600px; margin: 0 auto; background: #0B1929; color: #E2EAF4; padding: 32px; border-radius: 12px;">
          <div style="margin-bottom: 24px;">
            <span style="color: #1D6CE8; font-size: 24px; font-weight: 800;">Blu</span><span style="color: #E2EAF4; font-size: 24px; font-weight: 800;">Trades</span>
          </div>
          <h2 style="color: #E2EAF4; margin-bottom: 8px;">New message!</h2>
          <p style="color: #8BA7BF; margin-bottom: 20px;">${data.senderName} sent you a message on BluTrades.</p>
          <div style="background: #112236; border-radius: 8px; padding: 20px; margin-bottom: 20px; border: 1px solid #1E3148;">
            <div style="color: #8BA7BF; font-size: 13px; margin-bottom: 8px;">${data.senderName} says:</div>
            <div style="color: #E2EAF4; font-style: italic;">"${data.messagePreview}"</div>
          </div>
          <a href="https://blutrades.app" style="background: #1D6CE8; color: #fff; padding: 12px 24px; border-radius: 8px; text-decoration: none; font-weight: 700; display: inline-block; margin-bottom: 20px;">Reply</a>
          <p style="color: #4F6880; font-size: 12px;">You're receiving this because you have an account on BluTrades.</p>
        </div>
      `;
    }
    
    await fetch("/api/send-email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ to: recipientEmail, subject, html, type })
    });
  } catch(e) {
    console.error("Email notification error:", e);
  }
};

/* ── PRO ANALYTICS COMPONENT ────────────────────────────────── */
function ProAnalytics({ user }) {
  const [stats, setStats] = useState(null);

  useEffect(()=>{
    if(!user?.id) return;
    loadAnalytics();
  },[user?.id]);

  const loadAnalytics = async() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth()-1, 1).toISOString();

    const [
      {count: totalBids},
      {count: monthBids},
      {count: acceptedBids},
      {data: reviews},
      {count: profileViews}
    ] = await Promise.all([
      supabase.from("bids").select("*",{count:"exact",head:true}).eq("contractor_id",user.id),
      supabase.from("bids").select("*",{count:"exact",head:true}).eq("contractor_id",user.id).gte("created_at",monthStart),
      supabase.from("bids").select("*",{count:"exact",head:true}).eq("contractor_id",user.id).eq("status","accepted"),
      supabase.from("reviews").select("rating").eq("contractor_id",user.id),
      supabase.from("bids").select("*",{count:"exact",head:true}).eq("contractor_id",user.id).gte("created_at",lastMonthStart),
    ]);

    const avgRating = reviews?.length>0 ? (reviews.reduce((s,r)=>s+r.rating,0)/reviews.length).toFixed(1) : "—";
    const winRate = totalBids>0 ? Math.round((acceptedBids/totalBids)*100) : 0;

    setStats({ totalBids, monthBids, acceptedBids, avgRating, winRate, totalReviews: reviews?.length||0 });
  };

  if(!stats) return null;

  return (
    <div style={{ marginTop:24, background:"linear-gradient(135deg,#0D1B2E,#111827)", border:"1px solid #7C3AED44", borderRadius:12, padding:"20px" }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
        <span style={{ fontSize:16 }}>⭐</span>
        <div style={{ fontWeight:700, fontSize:15, color:"#A78BFA" }}>Pro Analytics</div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
        {[
          ["Bids This Month", stats.monthBids, "#1D6CE8"],
          ["Total Accepted", stats.acceptedBids, "#0D9488"],
          ["Win Rate", stats.winRate+"%", "#F59E0B"],
          ["Avg Rating", stats.avgRating, "#A78BFA"],
          ["Total Reviews", stats.totalReviews, "#60A5FA"],
          ["Total Bids", stats.totalBids, "#8BA7BF"],
        ].map(([label,val,color],i)=>(
          <div key={i} style={{ background:"#0A1628", borderRadius:8, padding:"12px", textAlign:"center", border:"1px solid #1E3148" }}>
            <div style={{ fontSize:22, fontWeight:800, color }}>{val}</div>
            <div style={{ fontSize:11, color:"#4A7FA5", marginTop:4, fontWeight:600 }}>{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── STRIPE PAYMENT COMPONENTS ──────────────────────────────── */
const STRIPE_PK = "pk_live_51TP4ZqAQUQA2HWS3MhIwoN0GUkvazEn6QeBaHycLf8jv4j0Wplim3Y6bOvxIIwauPrsL1lG7xyXIueKSlrvEjbxE00fhOzmKVL";
const STRIPE_PRO_PRICE_ID = "price_1TS4OQAQUQA2HWS3MGzyc800";

function ProUpgradeModal({ open, onClose, user }) {
  const [loading, setLoading] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const cardRef = useRef(null);
  const stripeRef = useRef(null);
  const cardElRef = useRef(null);

  useEffect(()=>{
    if(!open) return;
    setError(""); setSuccess(false);
    // Load Stripe.js
    const loadStripe = async() => {
      if(!window.Stripe) {
        const script = document.createElement("script");
        script.src = "https://js.stripe.com/v3/";
        script.onload = () => initStripe();
        document.head.appendChild(script);
      } else {
        initStripe();
      }
    };
    const initStripe = () => {
      stripeRef.current = window.Stripe(STRIPE_PK);
      const elements = stripeRef.current.elements();
      const card = elements.create("card", {
        style: {
          base: {
            color: "#E2EAF4",
            fontFamily: "DM Sans, sans-serif",
            fontSize: "15px",
            "::placeholder": { color: "#4F6880" },
          },
          invalid: { color: "#DC2626" }
        }
      });
      setTimeout(()=>{
        if(cardRef.current) {
          card.mount(cardRef.current);
          card.on("change", e=>{ setCardReady(e.complete); setError(e.error?.message||""); });
          cardElRef.current = card;
        }
      }, 100);
    };
    loadStripe();
    return () => { try { cardElRef.current?.destroy(); } catch(e) {} };
  },[open]);

  const handleSubscribe = async() => {
    if(!cardReady || !stripeRef.current) return;
    setLoading(true); setError("");
    try {
      // Create payment method
      const {paymentMethod, error: pmError} = await stripeRef.current.createPaymentMethod({
        type: "card",
        card: cardElRef.current,
        billing_details: { email: user?.email }
      });
      if(pmError) { setError(pmError.message); setLoading(false); return; }

      // Call backend to create real Stripe subscription
      const subRes = await fetch("/api/create-subscription", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          paymentMethodId: paymentMethod.id,
          email: user?.email,
          userId: user?.id
        })
      });

      const subData = await subRes.json();

      if(!subRes.ok || subData.error) {
        setError(subData.error || "Subscription failed. Please try again.");
        setLoading(false);
        return;
      }

      // Handle 3D Secure if required
      if(subData.requiresAction) {
        const {error: confirmError} = await stripeRef.current.confirmCardPayment(subData.clientSecret);
        if(confirmError) {
          setError(confirmError.message);
          setLoading(false);
          return;
        }
      }

      // Save pro status to Supabase
      await supabase.from("profiles").update({ 
        is_pro: true, 
        stripe_payment_method: paymentMethod.id,
        stripe_customer_id: subData.customerId,
        stripe_subscription_id: subData.subscriptionId
      }).eq("id", user?.id);
      
      setSuccess(true);
      setLoading(false);
      setTimeout(()=>window.location.reload(), 2500);

    } catch(e) {
      setError("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  if(!open) return null;
  return (
    <div style={{ position:"fixed", inset:0, background:"#00000099", display:"flex", alignItems:"center", justifyContent:"center", zIndex:500, backdropFilter:"blur(4px)", padding:20 }}>
      <div style={{ background:T.surface, borderRadius:16, padding:"32px", width:"100%", maxWidth:460, border:`1px solid ${T.border}`, boxShadow:"0 20px 60px #00000066" }}>
        {!success ? <>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
            <div>
              <div style={{ fontWeight:800, fontSize:22, color:T.text, letterSpacing:-.5 }}>BluTrades Pro</div>
              <div style={{ color:T.textMuted, fontSize:14, marginTop:2 }}>Unlock your full potential</div>
            </div>
            <button onClick={onClose} style={{ background:"none", border:"none", color:T.textMuted, fontSize:20, cursor:"pointer" }}>×</button>
          </div>

          {/* Price */}
          <div style={{ background:T.card, borderRadius:12, padding:"20px", marginBottom:20, border:`1px solid ${T.border}`, textAlign:"center" }}>
            <div style={{ fontSize:48, fontWeight:800, color:T.text, letterSpacing:-2 }}>$29<span style={{ fontSize:18, fontWeight:400, color:T.textMuted }}>/mo</span></div>
            <div style={{ color:T.textMuted, fontSize:13, marginTop:4 }}>Cancel anytime</div>
          </div>

          {/* Features */}
          <div style={{ marginBottom:20 }}>
            {[
              "Priority placement in search results",
              "Unlimited bids per month",
              "Verified Pro badge on profile",
              "Advanced analytics dashboard",
              "Priority customer support",
              "Featured in Top Contractors"
            ].map((f,i)=>(
              <div key={i} style={{ display:"flex", gap:10, alignItems:"center", padding:"7px 0", borderBottom:i<5?`1px solid ${T.border}33`:"none" }}>
                <span style={{ color:T.success, fontSize:14, flexShrink:0 }}>✓</span>
                <span style={{ color:T.textSub, fontSize:14 }}>{f}</span>
              </div>
            ))}
          </div>

          {/* Card input */}
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:12, color:"#C8DCF0", marginBottom:8, fontWeight:700, letterSpacing:.6, textTransform:"uppercase" }}>Card Details</div>
            <div ref={cardRef} style={{ background:T.card, border:`1px solid ${error?T.danger:T.border}`, borderRadius:8, padding:"14px 16px" }}/>
            {error && <div style={{ color:T.danger, fontSize:12, marginTop:6 }}>{error}</div>}
          </div>

          <div style={{ color:T.textMuted, fontSize:12, marginBottom:16, display:"flex", gap:6, alignItems:"center" }}>
            <span>🔒</span> Secured by Stripe. We never store your card details.
          </div>

          <button
            onClick={handleSubscribe}
            disabled={!cardReady || loading}
            style={{ width:"100%", background:cardReady&&!loading?T.blue:"#1E2E42", border:"none", borderRadius:8, padding:"14px", color:"#fff", fontSize:15, fontWeight:700, cursor:cardReady&&!loading?"pointer":"not-allowed", opacity:cardReady&&!loading?1:0.6 }}
          >
            {loading ? "Processing…" : "Subscribe — $29/month"}
          </button>
        </> : (
          <div style={{ textAlign:"center", padding:"20px 0" }}>
            <div style={{ fontSize:56, marginBottom:16 }}>🎉</div>
            <div style={{ fontWeight:800, fontSize:24, color:T.text, marginBottom:8 }}>Welcome to Pro!</div>
            <div style={{ color:T.textMuted, fontSize:15, marginBottom:24 }}>Your profile is now verified and boosted in search results.</div>
            <button onClick={onClose} style={{ background:T.blue, border:"none", borderRadius:8, padding:"12px 32px", color:"#fff", fontSize:15, fontWeight:700, cursor:"pointer" }}>Let's Go</button>
          </div>
        )}
      </div>
    </div>
  );
}

function PlatformFeeModal({ open, onClose, job, bid, user, onConfirm }) {
  const [loading, setLoading] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const cardRef = useRef(null);
  const stripeRef = useRef(null);
  const cardElRef = useRef(null);

  const feeAmount = Math.round((bid?.amount || 0) * 0.05); // 5% platform fee
  const totalAmount = (bid?.amount || 0) + feeAmount;

  useEffect(()=>{
    if(!open) return;
    setError(""); setSuccess(false); setCardReady(false);
    const init = () => {
      stripeRef.current = window.Stripe(STRIPE_PK);
      const elements = stripeRef.current.elements();
      const card = elements.create("card", {
        style: {
          base: { color:"#E2EAF4", fontFamily:"DM Sans, sans-serif", fontSize:"15px", "::placeholder":{ color:"#4F6880" } },
          invalid: { color:"#DC2626" }
        }
      });
      setTimeout(()=>{
        if(cardRef.current) {
          card.mount(cardRef.current);
          card.on("change", e=>{ setCardReady(e.complete); setError(e.error?.message||""); });
          cardElRef.current = card;
        }
      }, 100);
    };
    if(window.Stripe) { init(); }
    else {
      const s = document.createElement("script");
      s.src = "https://js.stripe.com/v3/";
      s.onload = init;
      document.head.appendChild(s);
    }
    return () => { try { cardElRef.current?.destroy(); } catch(e) {} };
  },[open]);

  const handlePay = async() => {
    if(!cardReady) return;
    setLoading(true); setError("");
    try {
      const {paymentMethod, error: pmError} = await stripeRef.current.createPaymentMethod({
        type:"card", card:cardElRef.current,
        billing_details:{ email: user?.email }
      });
      if(pmError) { setError(pmError.message); setLoading(false); return; }
      // In production: send paymentMethod.id to your backend to charge
      setSuccess(true);
      setLoading(false);
      if(onConfirm) onConfirm();
    } catch(e) {
      setError("Payment failed. Please try again.");
      setLoading(false);
    }
  };

  if(!open) return null;
  return (
    <div style={{ position:"fixed", inset:0, background:"#00000099", display:"flex", alignItems:"center", justifyContent:"center", zIndex:500, backdropFilter:"blur(4px)", padding:20 }}>
      <div style={{ background:T.surface, borderRadius:16, padding:"32px", width:"100%", maxWidth:440, border:`1px solid ${T.border}`, boxShadow:"0 20px 60px #00000066" }}>
        {!success ? <>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
            <div style={{ fontWeight:800, fontSize:20, color:T.text }}>Confirm & Pay</div>
            <button onClick={onClose} style={{ background:"none", border:"none", color:T.textMuted, fontSize:20, cursor:"pointer" }}>×</button>
          </div>

          {/* Job summary */}
          <div style={{ background:T.card, borderRadius:10, padding:"16px", marginBottom:20, border:`1px solid ${T.border}` }}>
            <div style={{ fontWeight:700, color:T.text, marginBottom:12, fontSize:15 }}>{job?.title}</div>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
              <span style={{ color:T.textMuted, fontSize:14 }}>Contractor bid</span>
              <span style={{ color:T.text, fontWeight:600 }}>${Number(bid?.amount||0).toLocaleString()}</span>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:10, paddingBottom:10, borderBottom:`1px solid ${T.border}` }}>
              <span style={{ color:T.textMuted, fontSize:14 }}>BluTrades fee (5%)</span>
              <span style={{ color:T.text, fontWeight:600 }}>${feeAmount.toLocaleString()}</span>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between" }}>
              <span style={{ color:T.text, fontWeight:700, fontSize:16 }}>Total</span>
              <span style={{ color:T.amber, fontWeight:800, fontSize:18 }}>${totalAmount.toLocaleString()}</span>
            </div>
          </div>

          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:12, color:"#C8DCF0", marginBottom:8, fontWeight:700, letterSpacing:.6, textTransform:"uppercase" }}>Payment Details</div>
            <div ref={cardRef} style={{ background:T.card, border:`1px solid ${error?T.danger:T.border}`, borderRadius:8, padding:"14px 16px" }}/>
            {error && <div style={{ color:T.danger, fontSize:12, marginTop:6 }}>{error}</div>}
          </div>

          <div style={{ color:T.textMuted, fontSize:12, marginBottom:16, display:"flex", gap:6, alignItems:"center" }}>
            <span>🔒</span> Secured by Stripe. Payment released to contractor on completion.
          </div>

          <button
            onClick={handlePay}
            disabled={!cardReady || loading}
            style={{ width:"100%", background:cardReady&&!loading?T.blue:"#1E2E42", border:"none", borderRadius:8, padding:"14px", color:"#fff", fontSize:15, fontWeight:700, cursor:cardReady&&!loading?"pointer":"not-allowed", opacity:cardReady&&!loading?1:0.6 }}
          >
            {loading ? "Processing…" : `Pay $${totalAmount.toLocaleString()}`}
          </button>
        </> : (
          <div style={{ textAlign:"center", padding:"20px 0" }}>
            <div style={{ fontSize:56, marginBottom:16 }}>✅</div>
            <div style={{ fontWeight:800, fontSize:22, color:T.text, marginBottom:8 }}>Payment Confirmed!</div>
            <div style={{ color:T.textMuted, fontSize:14, marginBottom:24 }}>The contractor has been notified. Payment will be released when the job is complete.</div>
            <button onClick={onClose} style={{ background:T.blue, border:"none", borderRadius:8, padding:"12px 32px", color:"#fff", fontSize:15, fontWeight:700, cursor:"pointer" }}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}


/* ── PWA SETUP ───────────────────────────────────────────────── */
// Register service worker
if('serviceWorker' in navigator) {
  window.addEventListener('load', ()=>{
    navigator.serviceWorker.register('/service-worker.js').catch(()=>{});
  });
}

// PWA Install Banner
function PWAInstallBanner() {
  const [prompt, setPrompt] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(()=>{
    const handler = e => { e.preventDefault(); setPrompt(e); setShow(true); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  },[]);

  if(!show) return null;
  return (
    <div style={{ position:"fixed", bottom:80, left:16, right:16, background:T.surface, border:`1px solid ${T.blue}44`, borderRadius:12, padding:"14px 16px", display:"flex", alignItems:"center", gap:12, zIndex:300, boxShadow:"0 8px 32px #00000066" }}>
      <div style={{ fontSize:28 }}>📲</div>
      <div style={{ flex:1 }}>
        <div style={{ fontWeight:700, color:T.text, fontSize:14 }}>Add BluTrades to Home Screen</div>
        <div style={{ color:T.textMuted, fontSize:12, marginTop:2 }}>Get the full app experience</div>
      </div>
      <button onClick={()=>{ prompt?.prompt(); setShow(false); }} style={{ background:T.blue, border:"none", borderRadius:7, padding:"8px 14px", color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer" }}>Install</button>
      <button onClick={()=>setShow(false)} style={{ background:"none", border:"none", color:T.textMuted, fontSize:18, cursor:"pointer", padding:"4px" }}>×</button>
    </div>
  );
}


/* ── SETTINGS MODALS ─────────────────────────────────────────── */
function PersonalInfoModal({ open, onClose, profile, user }) {
  const [pName, setPName] = useState(profile?.full_name||"");
  const [pPhone, setPPhone] = useState(profile?.phone||"");
  const [pLoc, setPLoc] = useState(profile?.location||"");
  const [saving, setSaving] = useState(false);

  useEffect(()=>{
    if(open) { setPName(profile?.full_name||""); setPPhone(profile?.phone||""); setPLoc(profile?.location||""); }
  },[open, profile]);

  const save = async() => {
    setSaving(true);
    await supabase.from("profiles").update({ full_name: pName, phone: pPhone, location: pLoc }).eq("id", user?.id);
    setSaving(false);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title="Personal Information">
      <Input label="Full Name" placeholder="Your full name" value={pName} onChange={e=>setPName(e.target.value)}/>
      <Input label="Email" placeholder="your@email.com" value={user?.email||""} onChange={()=>{}} type="email"/>
      <Input label="Phone Number" placeholder="+1 (555) 000-0000" value={pPhone} onChange={e=>setPPhone(e.target.value)}/>
      <Input label="City & State" placeholder="Asheville, NC" value={pLoc} onChange={e=>setPLoc(e.target.value)}/>
      <Btn variant="primary" full onClick={save} style={{padding:"12px"}}>{saving?"Saving...":"Save Changes"}</Btn>
    </Modal>
  );
}

function NotifModal({ open, onClose }) {
  const [prefs, setPrefs] = useState({messages:true,bids:true,jobs:true,marketing:false});
  const toggle = key => setPrefs(p=>({...p,[key]:!p[key]}));

  return (
    <Modal open={open} onClose={onClose} title="Notification Preferences">
      {[
        ["messages","New messages","Get notified when someone messages you"],
        ["bids","New bids","Get notified when contractors bid on your jobs"],
        ["jobs","Job updates","Updates on jobs you're tracking"],
        ["marketing","Marketing emails","Tips, updates and news from BluTrades"]
      ].map(([key,label,sub])=>(
        <div key={key} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 0",borderBottom:`1px solid ${T.border}`}}>
          <div><div style={{fontWeight:600,color:T.text,fontSize:14}}>{label}</div><div style={{color:T.textMuted,fontSize:12,marginTop:2}}>{sub}</div></div>
          <div onClick={()=>toggle(key)} style={{width:44,height:24,borderRadius:12,background:prefs[key]?T.blue:T.border,cursor:"pointer",position:"relative",flexShrink:0,transition:"background .2s"}}>
            <div style={{width:18,height:18,borderRadius:"50%",background:"#fff",position:"absolute",top:3,left:prefs[key]?23:3,transition:"left .2s"}}/>
          </div>
        </div>
      ))}
      <Btn variant="primary" full onClick={onClose} style={{marginTop:16,padding:"12px"}}>Save Preferences</Btn>
    </Modal>
  );
}

/* ══════════════════════════════════════════════════════════════
   MAIN APP
══════════════════════════════════════════════════════════════ */
export default function BluTrades() {
  const [session,setSession]=useState(null);
  const [user,setUser]=useState(null);
  const [profile,setProfile]=useState(null);
  const [loading,setLoading]=useState(true);
  const [tab,setTab]=useState("");
  const [selectedContractor,setSelectedContractor]=useState(null);
  const [selectedJob,setSelectedJob]=useState(null);
  const [activeChat,setActiveChat]=useState(null);
  const [contractOpen,setContractOpen]=useState(false);
  const [vouchOpen,setVouchOpen]=useState(false);
  const [proModalOpen,setProModalOpen]=useState(false);
  const [payModalOpen,setPayModalOpen]=useState(false);
  const [payJob,setPayJob]=useState(null);
  const [payBid,setPayBid]=useState(null);

  const [unreadMessages,setUnreadMessages]=useState(0);
  const [needsLocation,setNeedsLocation]=useState(false);
  const [showLegal,setShowLegal]=useState(null);
  const [notifications,setNotifications]=useState([]);
  const [showNotifs,setShowNotifs]=useState(false);
  const [unreadNotifs,setUnreadNotifs]=useState(0);
  const [locationInput,setLocationInput]=useState("");
  const [savingLocation,setSavingLocation]=useState(false);
  const isMobile=useIsMobile();

  const saveLocation=async()=>{
    if(!locationInput.trim()) return;
    setSavingLocation(true);
    await supabase.from("profiles").update({location:locationInput.trim()}).eq("id",user.id);
    setProfile(prev=>({...prev,location:locationInput.trim()}));
    setNeedsLocation(false);
    setSavingLocation(false);
  };

  const role=profile?.role||(tab.startsWith("cl_")?"client":"contractor");
  const isClient=tab.startsWith("cl_");
  const setSelected=(type,val)=>{if(type==="contractor")setSelectedContractor(val);else setSelectedJob(val);};

  useEffect(()=>{
    supabase.auth.getSession().then(({data:{session}})=>{
      setSession(session);
      if(session?.user) loadProfile(session.user);
      else setLoading(false);
    });
    const {data:{subscription}}=supabase.auth.onAuthStateChange((_event,session)=>{
      setSession(session);
      if(session?.user) loadProfile(session.user);
      else{setUser(null);setProfile(null);setLoading(false);}
    });
    return()=>subscription.unsubscribe();
  },[]);

  const loadProfile=async(authUser)=>{
    setUser(authUser);
    try{
      const{data}=await supabase.from("profiles").select("*").eq("id",authUser.id).single();
      setProfile(data);
      if(data?.role==="client") setTab("cl_home");
      else setTab("c_home");
      // Load unread messages
      loadUnreadCount(authUser.id);
      // Load notifications
      loadNotifications(authUser.id);
      // Check if location is set
      if (!data?.location) setNeedsLocation(true);
    }catch(err){console.error(err);}
    setLoading(false);
  };

  const loadNotifications=async(userId)=>{
    if(!userId) return;
    const {data:msgNotifs} = await supabase.from("messages").select("*, profiles:sender_id(full_name)").eq("recipient_id",userId).eq("read",false).order("created_at",{ascending:false}).limit(10);
    const notifs = (msgNotifs||[]).map(m=>({id:"msg_"+m.id,type:"message",title:"New message",body:`${m.profiles?.full_name||"Someone"} sent you a message`,time:m.created_at,read:false,icon:"💬"}));
    setNotifications(notifs);
    setUnreadNotifs(notifs.length);
  };

  const loadUnreadCount=async(userId)=>{
    if(!userId) return;
    const{count}=await supabase.from("messages").select("*",{count:"exact",head:true}).eq("recipient_id",userId).eq("read",false);
    setUnreadMessages(count||0);
  };

  // Poll unread count every 10 seconds
  useEffect(()=>{
    if(!user) return;
    const interval=setInterval(()=>loadUnreadCount(user.id),5000);
    return()=>clearInterval(interval);
  },[user?.id]);

  const handleAuth=(authUser,userProfile)=>{
    setUser(authUser);setProfile(userProfile);
    if(userProfile?.role==="client") setTab("cl_home");
    else setTab("c_home");
    if(!userProfile?.location) setNeedsLocation(true);
  };

  const handleSignOut=async()=>{
    await supabase.auth.signOut();
    setUser(null);setProfile(null);setSession(null);setTab("");
  };

  if(loading) return(
    <div style={{ minHeight:"100vh", background:T.bg, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ fontWeight:800, fontSize:28, color:T.text, letterSpacing:-1, marginBottom:12 }}>Blu<span style={{ color:T.blue }}>Trades</span></div>
        <div style={{ color:T.textMuted, fontSize:14 }}>Loading…</div>
      </div>
    </div>
  );

  if(!session) return(
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&display=swap'); *{box-sizing:border-box;margin:0;padding:0} body{font-family:'DM Sans',sans-serif} input,textarea,button{font-family:inherit} input::placeholder,textarea::placeholder{color:${T.textMuted}} button:hover{opacity:.85}`}</style>
      <AuthScreen onAuth={handleAuth}/>
    </>
  );

  const pp={setTab,setSelected,isMobile};
  const up={user,profile};

  const content=(
    <>
      {tab==="c_home"         &&<CDashboard    {...pp} {...up}/>}
      {tab==="c_jobs"         &&<CJobs         {...pp} profile={profile}/>}
      {tab==="c_jobDetail"    &&<CJobDetail    job={selectedJob} setTab={setTab} isMobile={isMobile} user={user}/>}
      {tab==="c_bids"         &&<CBids         setTab={setTab} isMobile={isMobile} user={user}/>}
      {tab==="c_tracker"      &&<CTracker      isMobile={isMobile}/>}
      {tab==="c_profile"      &&<ProfilePage   {...up} role="contractor" isMobile={isMobile} onSignOut={handleSignOut} onShowLegal={setShowLegal} onUpgradePro={()=>setProModalOpen(true)}/>}
      {tab==="cl_home"        &&<CLDashboard   {...pp} {...up} user={user}/>}
      {tab==="cl_post"        &&<CLPostJob     setTab={setTab} isMobile={isMobile} user={user}/>}
      {tab==="cl_reviewBids"  &&<CLReviewBids  job={selectedJob} setTab={setTab} openContract={()=>setContractOpen(true)} isMobile={isMobile} user={user} setActive={setActiveChat}/>}
      {tab==="cl_browse"      &&<CLBrowse      {...pp}/>}
      {tab==="cl_proDetail"   &&<CLProDetail   c={selectedContractor} setTab={setTab} openContract={()=>setContractOpen(true)} openVouch={()=>setVouchOpen(true)} user={user} setActive={setActiveChat} isMobile={isMobile}/>}
      {tab==="cl_projects"    &&<CLProjects    setTab={setTab} isMobile={isMobile} user={user}/>}
      {tab==="cl_profile"     &&<ProfilePage   {...up} role="client" isMobile={isMobile} onSignOut={handleSignOut} onShowLegal={setShowLegal}/>}
      {tab==="contracts"      &&<ContractsPage openContract={()=>setContractOpen(true)} isMobile={isMobile}/>}
      <div style={{display:tab==="messages"?"block":"none"}}><MessagesPage  setTab={setTab} setActive={setActiveChat} user={user} isMobile={isMobile} onRead={()=>loadUnreadCount(user?.id)}/></div>
      {tab==="chat"           &&<ChatPage      chat={activeChat} setTab={setTab} user={user}/>}
      
      
    </>
  );

  return(
    <div style={{ fontFamily:"'DM Sans',sans-serif", background:T.bg, minHeight:"100vh", color:T.text }} onClick={(e)=>{ if(showNotifs && !e.target.closest('.notif-bell')) setShowNotifs(false); }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{width:4px} ::-webkit-scrollbar-thumb{background:${T.border};border-radius:2px}
        input,textarea,button{font-family:'DM Sans',sans-serif}
        input::placeholder,textarea::placeholder{color:${T.textMuted}}
        button:hover{opacity:.85}
      `}</style>

      {isMobile?(
        <div style={{ paddingBottom:64 }}>
          {tab!=="chat"&&(
            <div style={{ background:`${T.surface}EE`, padding:"14px 16px", borderBottom:`1px solid ${T.border}`, position:"sticky", top:0, zIndex:100, backdropFilter:"blur(12px)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ fontWeight:800, fontSize:18, color:T.text, letterSpacing:-.5 }}>Blu<span style={{ color:T.blue }}>Trades</span></div>
              <div style={{ display:"flex", gap:5 }}>
                <button onClick={()=>setTab("c_home")} style={{ background:tab.startsWith("c_")&&!tab.startsWith("cl_")?"#2563EB":"transparent", color:tab.startsWith("c_")&&!tab.startsWith("cl_")?"#fff":"#6B8BA4", border:`1px solid ${tab.startsWith("c_")&&!tab.startsWith("cl_")?"#2563EB":"#2A3F58"}`, borderRadius:7, padding:"5px 10px", fontSize:10, fontWeight:700, cursor:"pointer", transition:"all .2s" }}>Contractor</button>
                <button onClick={()=>setTab("cl_home")} style={{ background:tab.startsWith("cl_")?"#2563EB":"transparent", color:tab.startsWith("cl_")?"#fff":"#6B8BA4", border:`1px solid ${tab.startsWith("cl_")?"#2563EB":"#2A3F58"}`, borderRadius:7, padding:"5px 10px", fontSize:10, fontWeight:700, cursor:"pointer", transition:"all .2s" }}>Client</button>
              </div>
            </div>
          )}
          <div style={{ padding:tab==="chat"?"0":"16px 14px 8px" }}>{content}</div>
          {tab!=="chat"&&<BottomNav tab={tab} setTab={setTab} role={role} unread={unreadMessages}/>}
        </div>
      ):(
        <div style={{ display:"flex", minHeight:"100vh" }}>
          <Sidebar tab={tab} setTab={setTab} role={role} profile={profile} onSignOut={handleSignOut} unread={unreadMessages} onShowLegal={setShowLegal}/>
          <div style={{ flex:1, display:"flex", flexDirection:"column", overflowY:"auto" }}>
            <div style={{ background:`${T.surface}EE`, padding:"13px 36px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between", alignItems:"center", position:"sticky", top:0, zIndex:50, backdropFilter:"blur(12px)" }}>
              <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                <div style={{ color:T.textMuted, fontSize:13 }}>{new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})}</div>
                <div style={{ background:isClient?"#0D9488":"#2563EB", border:"none", borderRadius:6, padding:"5px 14px", fontSize:13, fontWeight:700, color:"#fff", letterSpacing:.3 }}>{isClient?"👤 Client View":"👷 Contractor View"}</div>
              </div>
              <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                <div style={{ color:T.textMuted, fontSize:13 }}>Signed in as {profile?.full_name||user?.email}</div>
                <div style={{ position:"relative" }}>
                  <button onClick={(e)=>{e.stopPropagation();setShowNotifs(s=>!s);}} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:7, padding:"7px 12px", color:T.textSub, fontSize:16, cursor:"pointer", position:"relative" }}>
                    {String.fromCodePoint(128276)}
                    {unreadNotifs>0&&<div style={{ position:"absolute", top:-6, right:-6, background:T.danger, borderRadius:"50%", width:18, height:18, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:10, fontWeight:700 }}>{unreadNotifs}</div>}
                  </button>
                  {showNotifs&&(
                    <div style={{ position:"absolute", right:0, top:"110%", width:300, background:T.surface, border:`1px solid ${T.border}`, borderRadius:12, boxShadow:"0 8px 32px #00000066", zIndex:200, overflow:"hidden" }}>
                      <div style={{ padding:"14px 16px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                        <div style={{ fontWeight:700, fontSize:14, color:T.text }}>Notifications</div>
                        <button onClick={()=>{setUnreadNotifs(0);setShowNotifs(false);}} style={{ background:"none", border:"none", color:T.blueLight, fontSize:12, cursor:"pointer", fontWeight:600 }}>Mark all read</button>
                      </div>
                      <div style={{ maxHeight:360, overflowY:"auto" }}>
                        {notifications.length===0
                          ?<div style={{ padding:"32px 16px", textAlign:"center", color:T.textMuted, fontSize:14 }}>No notifications yet</div>
                          :notifications.map((n,i)=>(
                            <div key={n.id} onClick={()=>{setShowNotifs(false);setTab(n.type==="message"?"messages":isClient?"cl_home":"c_home");}} style={{ padding:"12px 16px", borderBottom:`1px solid ${T.border}`, cursor:"pointer", background:!n.read?T.blueFaint:"transparent", display:"flex", gap:12, alignItems:"flex-start" }}>
                              <div style={{ fontSize:18, flexShrink:0 }}>{n.icon}</div>
                              <div style={{ flex:1 }}>
                                <div style={{ fontWeight:600, fontSize:13, color:T.text, marginBottom:2 }}>{n.title}</div>
                                <div style={{ color:T.textMuted, fontSize:12 }}>{n.body}</div>
                              </div>
                              {!n.read&&<div style={{ width:7, height:7, borderRadius:"50%", background:T.blue, flexShrink:0, marginTop:4 }}/>}
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  )}
                </div>
                <button onClick={handleSignOut} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:7, padding:"7px 14px", color:T.textSub, fontSize:13, fontWeight:600, cursor:"pointer" }}>Sign Out</button>
                {isClient?<Btn variant="teal" onClick={()=>setTab("cl_post")} style={{ padding:"8px 18px" }}>Post a Job</Btn>:<Btn variant="primary" onClick={()=>setContractOpen(true)} style={{ padding:"8px 18px" }}>New Contract</Btn>}
              </div>
            </div>
            <div style={{ flex:1, padding:"32px 36px 48px", maxWidth:1160 }}>{content}</div>
          </div>
        </div>
      )}

      <ContractModal open={contractOpen} onClose={()=>setContractOpen(false)}/>
      <VouchModal open={vouchOpen} onClose={()=>setVouchOpen(false)} contractor={selectedContractor}/>
      <ProUpgradeModal open={proModalOpen} onClose={()=>setProModalOpen(false)} user={user}/>
      <PlatformFeeModal open={payModalOpen} onClose={()=>setPayModalOpen(false)} job={payJob} bid={payBid} user={user} onConfirm={()=>setPayModalOpen(false)}/>

      <PWAInstallBanner/>
      {/* Legal Pages Overlay */}
      {showLegal && (
        <div style={{ position:"fixed", inset:0, background:T.bg, zIndex:600, overflowY:"auto", padding:"32px 24px" }}>
          <div style={{ maxWidth:720, margin:"0 auto" }}>
            <button onClick={()=>setShowLegal(null)} style={{ background:"none", border:"none", color:T.blueLight, fontSize:14, fontWeight:600, cursor:"pointer", marginBottom:28, display:"flex", alignItems:"center", gap:6 }}>← Back to BluTrades</button>
            <div style={{ fontWeight:800, fontSize:32, color:T.text, letterSpacing:-.5, marginBottom:8 }}>{showLegal==="terms"?"Terms of Service":"Privacy Policy"}</div>
            <div style={{ color:T.textMuted, fontSize:14, marginBottom:32 }}>Last updated: April 24, 2026</div>
            {(showLegal==="terms"?[
              ["1. Acceptance of Terms","By accessing or using BluTrades (blutrades.app), you agree to be bound by these Terms of Service. If you do not agree, do not use our platform."],
              ["2. Description of Service","BluTrades is a marketplace platform that connects contractors and tradespeople with homeowners and general contractors. We facilitate job posting, bidding, contract generation, messaging, and payment processing."],
              ["3. User Accounts","You must provide accurate information when creating an account. You are responsible for maintaining the confidentiality of your account credentials. You must be at least 18 years old to use BluTrades."],
              ["4. Contractor Responsibilities","Contractors represent that all credentials, licenses, and insurance information provided is accurate and current. Contractors are solely responsible for the quality of their work and compliance with all applicable laws."],
              ["5. Client Responsibilities","Clients are responsible for providing accurate job descriptions and having the legal right to authorize the work requested. Clients must make payment through BluTrades for jobs facilitated through the platform."],
              ["6. Platform Fees","BluTrades charges a service fee on transactions processed through the platform. Fees are non-refundable except as required by law."],
              ["7. Payments","All payments must be processed through BluTrades. BluTrades uses Stripe for payment processing and does not store card information."],
              ["8. Prohibited Conduct","Users may not post false or misleading information, circumvent platform fees, harass other users, or use the platform for illegal activities."],
              ["9. Limitation of Liability","BluTrades is not liable for the quality of contractor work or disputes between users beyond the fees paid to BluTrades in the preceding 12 months."],
              ["10. Contact","For questions about these terms, contact us at support@blutrades.app"],
            ]:[
              ["Information We Collect","We collect your name, email, location, trade, job postings, messages, credential documents, and usage data when you use BluTrades."],
              ["How We Use Your Information","We use your information to operate BluTrades, match contractors with jobs, facilitate communication, process payments, and improve the platform."],
              ["Information Sharing","We do not sell your personal information. We share data with other users to facilitate the marketplace, with Stripe for payments, and with law enforcement when required."],
              ["Data Security","All data is stored with industry-standard encryption on Supabase infrastructure. Payment data is handled exclusively by Stripe and never stored on BluTrades servers."],
              ["Your Rights","You may access, update, or delete your account information at any time. Contact support@blutrades.app for data requests."],
              ["Cookies","We use essential cookies to keep you logged in. We do not use advertising cookies or sell browsing data."],
              ["Data Retention","We retain account data while your account is active and transaction records for 7 years as required by law."],
              ["Children's Privacy","BluTrades is not intended for users under 18. We do not knowingly collect information from minors."],
              ["Contact Us","For privacy questions, contact support@blutrades.app"],
            ]).map(([title,body],i)=>(
              <Card key={i} style={{ marginBottom:10 }}>
                <div style={{ fontWeight:700, fontSize:15, color:T.text, marginBottom:8 }}>{title}</div>
                <div style={{ color:T.textSub, fontSize:14, lineHeight:1.8 }}>{body}</div>
              </Card>
            ))}
            <div style={{ textAlign:"center", marginTop:24, color:T.textMuted, fontSize:13 }}>
              Questions? Email <a href="mailto:support@blutrades.app" style={{ color:T.blueLight }}>support@blutrades.app</a>
            </div>
          </div>
        </div>
      )}

      {/* Force location setup */}
      {needsLocation && (
        <div style={{ position:"fixed", inset:0, background:"#000000CC", display:"flex", alignItems:"center", justifyContent:"center", zIndex:500, backdropFilter:"blur(6px)", padding:24 }}>
          <div style={{ background:T.surface, borderRadius:14, padding:"36px 32px", width:"100%", maxWidth:440, border:`1px solid ${T.border}`, boxShadow:"0 24px 80px #000000AA" }}>
            <div style={{ textAlign:"center", marginBottom:24 }}>
              <div style={{ fontSize:40, marginBottom:12 }}>📍</div>
              <div style={{ fontWeight:700, fontSize:22, color:T.text, marginBottom:8 }}>Where are you located?</div>
              <div style={{ color:T.textMuted, fontSize:14, lineHeight:1.6 }}>We use your location to show you relevant jobs and contractors in your area. You can change this anytime in your profile.</div>
            </div>
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:12, color:"#C8DCF0", marginBottom:6, fontWeight:700, letterSpacing:.6, textTransform:"uppercase" }}>City & State</div>
              <input
                value={locationInput}
                onChange={e=>setLocationInput(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&saveLocation()}
                placeholder="e.g. Asheville, NC"
                autoFocus
                style={{ width:"100%", background:T.card, border:`1px solid ${T.border}`, borderRadius:8, padding:"12px 16px", color:T.text, fontSize:15, outline:"none" }}
              />
            </div>
            <button onClick={saveLocation} disabled={!locationInput.trim()||savingLocation} style={{ width:"100%", background:T.blue, color:"#fff", border:"none", borderRadius:8, padding:"13px", fontSize:15, fontWeight:700, cursor:locationInput.trim()?"pointer":"not-allowed", opacity:locationInput.trim()?1:0.5 }}>
              {savingLocation?"Saving...":"Set My Location"}
            </button>
            <button onClick={()=>setNeedsLocation(false)} style={{ width:"100%", background:"none", border:"none", color:T.textMuted, fontSize:13, cursor:"pointer", marginTop:10, padding:"6px" }}>
              Skip for now
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
